import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { useMsal } from '@azure/msal-react';
import scheduleGeneratorService from '../services/scheduleGeneratorService';
import aiScheduleService from '../services/aiScheduleService';
import buildMockAnalysisDataset from '../services/mockDataProvider';
import {
  analyzeHistoricalData,
  applyAnnualVolumeTarget,
  calculateAnnualOfferingTarget,
  generateDraftSchedule,
  detectConflicts,
  generateAnalyticsSummary
} from '../utils/scheduleAlgorithm';
import {
  saveDraft,
  getAllDrafts,
  getDraft,
  deleteDraft,
  importDraftFromJSON,
  saveAnalysisCache,
  getAnalysisCache
} from '../utils/draftStorage';
import { getHolidaysForYears, getTeachingDatesInRange, isValidTeachingBlock } from '../utils/holidays';
import { DEPARTMENT_COLORS, getDepartmentColor } from '../utils/departmentColors';
import {
  loadPlanningRules,
  savePlanningRules,
  DEFAULT_EXCLUDE_KEYWORDS,
  RULE_TYPES,
  RULE_TYPE_META,
  BUILT_IN_SCHEDULING_RULES,
  makeRuleId,
  describeRule,
  filterSchedulingRulesForCourses,
  normalizePlanningRules,
  validateSchedulingRules,
  summarizeRulesForAI
} from '../utils/schedulingRules';
import {
  acceptSharedPlanningRulesSnapshot,
  fetchSharedPlanningRules,
  getPlanningRulesSyncError,
  pushSharedPlanningRules
} from '../utils/planningRulesSync';
import * as XLSX from 'xlsx';
import { buildRevenueReport, revenueReportToWorkbookData, createPriceResolver, buildHubSanityReport, hubSanityToWorkbookData } from '../utils/revenueReport';

const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK_DATA === 'true';

// PROD SAFETY FLAGS — flip to false to re-enable when seed data is ready.
// New course filter added in prod (cannot be undone) means we MUST NOT push
// generator output to Dataverse until the data model is reconciled.
const PUSH_TO_PA_DISABLED = true;
const HIDE_AI_TAB = true;

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const MONTH_FULL = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const getDefaultSessionDates = (year) => {
  const monday = new Date(year, 0, 1);
  monday.setDate(monday.getDate() + ((8 - monday.getDay()) % 7));
  const holidays = getHolidaysForYears([year]);
  while (!isValidTeachingBlock(monday, 5, holidays)) {
    monday.setDate(monday.getDate() + 7);
  }
  const friday = new Date(monday);
  friday.setDate(friday.getDate() + 4);
  const format = date => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  return { startDate: format(monday), endDate: format(friday) };
};

const ScheduleGenerator = ({ userRole, permissions = {}, roleChecking }) => {
  const { accounts } = useMsal();
  const account = accounts[0];

  // Data states
  const [analysisData, setAnalysisData] = useState(null);
  const [courseProfiles, setCourseProfiles] = useState([]);
  const [draftSessions, setDraftSessions] = useState([]);
  const [savedDrafts, setSavedDrafts] = useState([]);
  const [conflicts, setConflicts] = useState([]);

  // UI states
  const [activeTab, setActiveTab] = useState('analyze');
  const [loading, setLoading] = useState(false);
  const [loadingPhase, setLoadingPhase] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [revenueExportingId, setRevenueExportingId] = useState(null);
  const [error, setError] = useState(null);
  const [generationError, setGenerationError] = useState(null);
  const [targetYear, setTargetYear] = useState(2027);
  const [selectedMonth, setSelectedMonth] = useState('all');
  const [searchFilter, setSearchFilter] = useState('');
  const [calendarDate, setCalendarDate] = useState(new Date(2027, 0, 1));
  const [calendarSelectedSession, setCalendarSelectedSession] = useState(null);
  const [publishingSessionIds, setPublishingSessionIds] = useState(new Set());
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSession, setNewSession] = useState({
    name: '',
    ...getDefaultSessionDates(2027),
    maxCapacity: 20,
    instructor: '',
    department: '',
    locationName: '',
    locationGuid: null,
    classroom: '',
    campus: ''
  });

  // AI states
  const [aiSuggestions, setAiSuggestions] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [managerFeedback, setManagerFeedback] = useState('');
  const [feedbackResponse, setFeedbackResponse] = useState('');

  // Organization-wide planning rules, with localStorage as the instant cache.
  const [planningRules, setPlanningRules] = useState(() => loadPlanningRules());
  const [newExcludeKeyword, setNewExcludeKeyword] = useState('');
  const [newPlanningNote, setNewPlanningNote] = useState('');
  const [ruleCourseNames, setRuleCourseNames] = useState(() =>
    (planningRules.courseProfileOptions || []).map(option => option.name).filter(Boolean)
  );
  const [ruleLocations, setRuleLocations] = useState([]);
  const [ruleOptionsLoading, setRuleOptionsLoading] = useState(false);
  const [ruleOptionsLoaded, setRuleOptionsLoaded] = useState(false);
  const [rulesSyncStatus, setRulesSyncStatus] = useState('loading');
  const rulesSaveSequenceRef = useRef(0);
  const rulesScopeRef = useRef('shared');
  const sharedPlanningRulesRef = useRef(planningRules);

  // Course Profiles table: search + sort controls (default: alphabetical by name)
  const [profileSearch, setProfileSearch] = useState('');
  const [profileSortField, setProfileSortField] = useState('name');
  const [profileSortDir, setProfileSortDir] = useState('asc');
  const [planningAdvice, setPlanningAdvice] = useState('');
  const [planningLoading, setPlanningLoading] = useState(false);
  const emptyRuleForm = { type: RULE_TYPES.MIN_GAP, severity: 'hard', courseA: '', courseB: '', courses: [], coursePicker: '', location: '', fromLocation: '', toLocation: '', gapDays: 14, targetYear: '', daysOfWeek: [] };
  const [newRule, setNewRule] = useState(emptyRuleForm);

  // Publishing states
  const [publishing, setPublishing] = useState(false);
  const [publishResults, setPublishResults] = useState(null);

  // Access to the generator: Schedulers, Schedule Admins, or IT Admins.
  // (permissions.isScheduleManager is true for any of those three roles.)
  // Fallback to the old `userRole === 'editor'` check only if no permissions
  // object was supplied (defensive — should not happen in normal app flow).
  const isScheduleManager = permissions.isScheduleManager ?? (userRole === 'editor');
  // Only Schedule Admins (and IT Admins) may publish to Dataverse / Power Apps.
  const canPublish = permissions.canPublish ?? false;

  const syncPlanningRules = useCallback((rules) => {
    const sequence = ++rulesSaveSequenceRef.current;
    setRulesSyncStatus('saving');
    return pushSharedPlanningRules(rules).then(success => {
      if (sequence === rulesSaveSequenceRef.current) {
        setRulesSyncStatus(success ? 'saved' : getPlanningRulesSyncError() === 'conflict' ? 'conflict' : 'error');
      }
      return success;
    });
  }, []);

  const refreshSharedPlanningRules = useCallback(async (replaceDraftRules = false) => {
    const sequenceAtStart = rulesSaveSequenceRef.current;
    setRulesSyncStatus('loading');
    const snapshot = await fetchSharedPlanningRules();
    // Do not let a slow GET replace a rule edited after the request started.
    if (sequenceAtStart !== rulesSaveSequenceRef.current) return false;
    if (rulesScopeRef.current === 'draft' && !replaceDraftRules) {
      setRulesSyncStatus('draft');
      return false;
    }
    if (!snapshot) {
      setRulesSyncStatus('error');
      return false;
    }
    const { rules: shared, etag } = snapshot;
    acceptSharedPlanningRulesSnapshot(etag);
    rulesScopeRef.current = 'shared';
    sharedPlanningRulesRef.current = shared;
    savePlanningRules(shared);
    setPlanningRules(shared);
    setRuleCourseNames((shared.courseProfileOptions || []).map(option => option.name).filter(Boolean));
    setRulesSyncStatus('saved');
    return true;
  }, []);

  const includedProfileOptions = useCallback((profiles, excludedKeys = planningRules.excludedCourseKeys) => {
    const excluded = new Set(excludedKeys || []);
    return (profiles || [])
      .filter(profile => !excluded.has(profile.key))
      .map(profile => ({ key: profile.key, name: profile.name }))
      .filter(option => option.key && option.name)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [planningRules.excludedCourseKeys]);

  const publishCourseProfileOptions = useCallback((profiles) => {
    const options = includedProfileOptions(profiles);
    const next = { ...planningRules, courseProfileOptions: options };
    setPlanningRules(next);
    setRuleCourseNames(options.map(option => option.name));
    if (rulesScopeRef.current === 'draft') {
      setRulesSyncStatus('draft');
    } else {
      sharedPlanningRulesRef.current = next;
      savePlanningRules(next);
      syncPlanningRules(next);
    }
  }, [includedProfileOptions, planningRules, syncPlanningRules]);

  // --- Data Loading ---
  const loadAnalysis = useCallback(async (forceRefresh = false) => {
    console.log('[ScheduleGenerator] loadAnalysis triggered');
    setLoading(true);
    setError(null);
    // Read manager-defined exclusions fresh so rule edits apply on re-analyze.
    const ruleExcludes = loadPlanningRules().excludeKeywords;

    try {
      if (USE_MOCK_DATA) {
        console.log('[ScheduleGenerator] MockMode: using synthetic data');
        setLoadingPhase('Loading mock data from resource parameters...');
        const result = buildMockAnalysisDataset(2, (phase, message) => {
          setLoadingPhase(message);
        });
        if (result.success) {
          console.log(`[ScheduleGenerator] Mock data returned ${result.data.sessions?.length || 0} sessions`);
          const loadedAnalysis = { ...result.data, analysisCache: { cachedAt: new Date().toISOString(), source: 'mock-data' } };
          setAnalysisData(loadedAnalysis);
          const profiles = analyzeHistoricalData(result.data.sessions, { excludeKeywords: ruleExcludes });
          console.log(`[ScheduleGenerator] Built ${profiles.length} course profiles`);
          setCourseProfiles(profiles);
          publishCourseProfileOptions(profiles);
          return { analysisData: loadedAnalysis, courseProfiles: profiles };
        } else {
          console.error('[ScheduleGenerator] Mock data load failed:', result.error);
          setError(result.error || 'Failed to load mock data');
        }
        return null;
      }

      // Reuse the browser's source-data snapshot for a workday. Rules and
      // exclusions are recomputed locally without another Dataverse download.
      console.log('[ScheduleGenerator] Checking cache...');
      const cached = forceRefresh ? null : await getAnalysisCache(8 * 60);
      if (cached) {
        console.log(`[ScheduleGenerator] Cache HIT (${cached.sessions?.length || 0} sessions)`);
        setAnalysisData(cached);
        const profiles = analyzeHistoricalData(cached.sessions, { excludeKeywords: ruleExcludes });
        console.log(`[ScheduleGenerator] Built ${profiles.length} course profiles from cache`);
        setCourseProfiles(profiles);
        publishCourseProfileOptions(profiles);
        return { analysisData: cached, courseProfiles: profiles };
      }
      console.log('[ScheduleGenerator] Cache MISS — calling buildAnalysisDataset');

      const result = await scheduleGeneratorService.buildAnalysisDataset(2, (phase, message) => {
        console.log(`[ScheduleGenerator] phase=${phase}: ${message}`);
        setLoadingPhase(message);
      });

      if (result.success) {
        console.log(`[ScheduleGenerator] buildAnalysisDataset OK — ${result.data.sessions?.length || 0} sessions`);
        const cacheRecord = await saveAnalysisCache(result.data);
        const cachedAt = cacheRecord?.cachedAt || new Date().toISOString();
        const loadedAnalysis = {
          ...result.data,
          analysisCache: { cachedAt, source: 'dataverse' }
        };
        setAnalysisData(loadedAnalysis);
        const profiles = analyzeHistoricalData(result.data.sessions, { excludeKeywords: ruleExcludes });
        console.log(`[ScheduleGenerator] Built ${profiles.length} course profiles`);
        setCourseProfiles(profiles);
        publishCourseProfileOptions(profiles);
        return { analysisData: loadedAnalysis, courseProfiles: profiles };
      } else {
        console.error('[ScheduleGenerator] buildAnalysisDataset FAILED:', result.error);
        setError(result.error || 'Failed to load analysis data');
        return null;
      }
    } catch (err) {
      console.error('[ScheduleGenerator] loadAnalysis exception:', err);
      setError(err.message || String(err));
      return null;
    } finally {
      setLoading(false);
      setLoadingPhase('');
    }
  }, [publishCourseProfileOptions]);

  const loadRuleOptions = useCallback(async () => {
    if (ruleOptionsLoading) return;
    setRuleOptionsLoading(true);
    setError(null);
    try {
      if (USE_MOCK_DATA) {
        const result = buildMockAnalysisDataset(2);
        if (!result.success) throw new Error(result.error || 'Failed to load mock rule options');
        const profiles = analyzeHistoricalData(result.data.sessions);
        setRuleCourseNames(profiles.map(profile => profile.name).filter(Boolean));
        setRuleLocations(result.data.locations || []);
      } else {
        let profileOptions;
        if (courseProfiles.length > 0) {
          profileOptions = includedProfileOptions(courseProfiles);
          // The currently displayed Analyze page is authoritative. Replace any
          // older shared snapshot instead of merging stale course names into it.
          publishCourseProfileOptions(courseProfiles);
        } else {
          profileOptions = planningRules.courseProfileOptions || [];
        }
        if (profileOptions.length === 0) {
          throw new Error('No shared Course Profile list is available yet. Run Analyze once to publish the initial list; future rule setup will not require analysis first.');
        }
        const result = await scheduleGeneratorService.getRuleOptions();
        if (!result.success) throw new Error(result.error || 'Failed to load course and location options');
        setRuleCourseNames(profileOptions.map(option => option.name).filter(Boolean));
        setRuleLocations(result.data.locations || []);
      }
      setRuleOptionsLoaded(true);
    } catch (err) {
      console.error('[ScheduleGenerator] loadRuleOptions exception:', err);
      setError(err.message || String(err));
    } finally {
      setRuleOptionsLoading(false);
    }
  }, [courseProfiles, includedProfileOptions, planningRules.courseProfileOptions, publishCourseProfileOptions, ruleOptionsLoading]);

  const generateDraft = useCallback(async () => {
    if (isGenerating) return;

    // generateDraftSchedule is a heavy synchronous computation that blocks the
    // main thread. Flip on the "generating" indicator first, then defer the
    // actual work to the next tick so the browser can paint the spinner before
    // the thread is blocked (otherwise the UI looks frozen with no feedback).
    setGenerationError(null);
    setIsGenerating(true);
    let profilesForGeneration = courseProfiles;
    let analysisForGeneration = analysisData;
    if (profilesForGeneration.length === 0 || !analysisForGeneration) {
      const loaded = await loadAnalysis(false);
      if (!loaded || loaded.courseProfiles.length === 0) {
        setGenerationError('Analysis data could not be loaded. Open Analyze and use Refresh from Dataverse, then try again.');
        setIsGenerating(false);
        return;
      }
      profilesForGeneration = loaded.courseProfiles;
      analysisForGeneration = loaded.analysisData;
    }
    setTimeout(() => {
      try {
        // Skip courses the manager toggled OFF (e.g. one-off special groups).
        const excludedKeys = new Set(planningRules.excludedCourseKeys || []);
        const includedProfiles = profilesForGeneration.filter(c => !excludedKeys.has(c.key));
        const balancingRules = filterSchedulingRulesForCourses(
          planningRules.schedulingRules,
          includedProfiles.map(course => course.name)
        );
        const annualOfferingTarget = calculateAnnualOfferingTarget(profilesForGeneration);
        const volumeBalancedProfiles = applyAnnualVolumeTarget(
          includedProfiles,
          annualOfferingTarget,
          balancingRules,
          targetYear
        );
        const applicableRules = filterSchedulingRulesForCourses(
          planningRules.schedulingRules,
          volumeBalancedProfiles.map(course => course.name)
        );

        const draft = generateDraftSchedule(
          volumeBalancedProfiles,
          targetYear,
          analysisForGeneration?.locations || [],
          applicableRules
        );
        setDraftSessions(draft);

        const newConflicts = detectConflicts(draft);
        setConflicts(newConflicts);

    // Generated drafts are unsaved by definition — detach from any previously
    // loaded saved draft so the next "Save" creates a new record (with prompt).
        setCurrentDraftId(null);
        setCurrentDraftName('');

        setActiveTab('draft');
      } catch (err) {
        console.error('Draft generation failed:', err);
        setGenerationError(err.message || String(err));
      } finally {
        setIsGenerating(false);
      }
    }, 50);
  }, [courseProfiles, targetYear, analysisData, planningRules.schedulingRules, planningRules.excludedCourseKeys, isGenerating, loadAnalysis]);

  // --- AI Integration ---
  const requestAISuggestions = useCallback(async () => {
    if (!analysisData || courseProfiles.length === 0) return;

    setAiLoading(true);
    try {
      const summary = generateAnalyticsSummary(courseProfiles, analysisData.sessions, { excludeKeywords: planningRules.excludeKeywords });
      const result = await aiScheduleService.getSuggestions(summary, courseProfiles, targetYear);

      if (result.success) {
        setAiSuggestions(result.data);
      } else {
        setAiSuggestions(`AI suggestions unavailable: ${result.error}`);
      }
    } catch (err) {
      setAiSuggestions(`Error: ${err.message}`);
    } finally {
      setAiLoading(false);
    }
  }, [analysisData, courseProfiles, targetYear, planningRules.excludeKeywords]);

  const submitFeedback = useCallback(async () => {
    if (!managerFeedback.trim() || draftSessions.length === 0) return;

    setAiLoading(true);
    try {
      const result = await aiScheduleService.refineSuggestions(
        draftSessions,
        managerFeedback,
        courseProfiles
      );

      if (result.success) {
        setFeedbackResponse(result.data);
      } else {
        setFeedbackResponse(`Unable to process feedback: ${result.error}`);
      }
    } catch (err) {
      setFeedbackResponse(`Error: ${err.message}`);
    } finally {
      setAiLoading(false);
    }
  }, [managerFeedback, draftSessions, courseProfiles]);

  // --- Planning Rules management ---
  const persistRules = useCallback((updater) => {
    setPlanningRules(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      if (rulesScopeRef.current === 'draft') {
        setRulesSyncStatus('draft');
      } else {
        sharedPlanningRulesRef.current = next;
        savePlanningRules(next);            // instant local cache
        syncPlanningRules(next);            // queued shared server save + visible status
      }
      return next;
    });
  }, [syncPlanningRules]);

  // Toggle a single course in/out of draft generation. Persisted immediately.
  const toggleCourseIncluded = useCallback((key) => {
    persistRules(prev => {
      const set = new Set(prev.excludedCourseKeys || []);
      if (set.has(key)) set.delete(key); else set.add(key);
      return { ...prev, excludedCourseKeys: [...set], courseProfileOptions: includedProfileOptions(courseProfiles, [...set]) };
    });
  }, [courseProfiles, includedProfileOptions, persistRules]);

  // Bulk include/exclude a set of course keys (used by the visible-list buttons).
  const setCoursesExcluded = useCallback((keys, excluded) => {
    persistRules(prev => {
      const set = new Set(prev.excludedCourseKeys || []);
      keys.forEach(k => { if (excluded) set.add(k); else set.delete(k); });
      return { ...prev, excludedCourseKeys: [...set], courseProfileOptions: includedProfileOptions(courseProfiles, [...set]) };
    });
  }, [courseProfiles, includedProfileOptions, persistRules]);

  // Click a sortable column header: same field flips direction; new field uses a
  // sensible default (names ascending A→Z, numbers/trend descending high→low).
  const handleProfileSort = useCallback((field) => {
    setProfileSortField(prevField => {
      if (prevField === field) {
        setProfileSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        return prevField;
      }
      setProfileSortDir(field === 'name' ? 'asc' : 'desc');
      return field;
    });
  }, []);

  const handleAddExcludeKeyword = useCallback(() => {
    const kw = newExcludeKeyword.trim();
    if (!kw) return;
    const exists = planningRules.excludeKeywords.some(k => k.toLowerCase() === kw.toLowerCase());
    const isDefault = DEFAULT_EXCLUDE_KEYWORDS.some(k => k.toLowerCase() === kw.toLowerCase());
    if (exists || isDefault) return;
    const excludeKeywords = [...planningRules.excludeKeywords, kw];
    const profiles = analysisData?.sessions
      ? analyzeHistoricalData(analysisData.sessions, { excludeKeywords })
      : courseProfiles;
    if (analysisData?.sessions) setCourseProfiles(profiles);
    persistRules({
      ...planningRules,
      excludeKeywords,
      courseProfileOptions: includedProfileOptions(profiles, planningRules.excludedCourseKeys)
    });
    setNewExcludeKeyword('');
  }, [analysisData, courseProfiles, includedProfileOptions, newExcludeKeyword, persistRules, planningRules]);

  const handleRemoveExcludeKeyword = useCallback((kw) => {
    const excludeKeywords = planningRules.excludeKeywords.filter(k => k !== kw);
    const profiles = analysisData?.sessions
      ? analyzeHistoricalData(analysisData.sessions, { excludeKeywords })
      : courseProfiles;
    if (analysisData?.sessions) setCourseProfiles(profiles);
    persistRules({
      ...planningRules,
      excludeKeywords,
      courseProfileOptions: includedProfileOptions(profiles, planningRules.excludedCourseKeys)
    });
  }, [analysisData, courseProfiles, includedProfileOptions, persistRules, planningRules]);

  const handleAddPlanningNote = useCallback(() => {
    const text = newPlanningNote.trim();
    if (!text) return;
    persistRules(prev => ({
      ...prev,
      planningNotes: [...prev.planningNotes, { id: `note-${Date.now()}`, text }]
    }));
    setNewPlanningNote('');
  }, [newPlanningNote, persistRules]);

  const handleRemovePlanningNote = useCallback((id) => {
    persistRules(prev => ({ ...prev, planningNotes: prev.planningNotes.filter(n => n.id !== id) }));
  }, [persistRules]);

  // --- Structured scheduling rules (enforced) ---
  const persistSchedulingRules = useCallback((schedulingRules) => {
    const errors = validateSchedulingRules(
      [...BUILT_IN_SCHEDULING_RULES, ...schedulingRules],
      { courseNames: [...ruleCourseNames, ...courseProfiles.map(course => course.name)] }
    );
    if (errors.length > 0) {
      alert(`This rule conflicts with the current rule set:\n\n• ${errors.join('\n• ')}`);
      return false;
    }
    persistRules(prev => ({ ...prev, schedulingRules }));
    return true;
  }, [courseProfiles, persistRules, ruleCourseNames]);

  const handleAddRule = useCallback(() => {
    const meta = RULE_TYPE_META[newRule.type];
    if (!meta) return;
    const fields = meta.fields;
    const rule = {
      id: makeRuleId(),
      type: newRule.type,
      severity: [RULE_TYPES.LOCATION_BLACKOUT, RULE_TYPES.LOCATION_REASSIGN].includes(newRule.type) ? 'hard' : newRule.severity,
      enabled: true
    };
    if (fields.includes('courseA')) rule.courseA = newRule.courseA.trim();
    if (fields.includes('courseB')) rule.courseB = newRule.courseB.trim();
    if (fields.includes('courses')) rule.courses = newRule.courses;
    if (fields.includes('daysOfWeek')) rule.daysOfWeek = newRule.daysOfWeek;
    if (fields.includes('location')) rule.location = newRule.location.trim();
    if (fields.includes('fromLocation')) rule.fromLocation = newRule.fromLocation.trim();
    if (fields.includes('toLocation')) rule.toLocation = newRule.toLocation.trim();
    if (fields.includes('gapDays')) rule.gapDays = Math.max(1, parseInt(newRule.gapDays, 10) || 14);
    if (fields.includes('targetYear')) rule.targetYear = newRule.targetYear ? Number(newRule.targetYear) : null;

    // Validate required fields are present.
    if (fields.includes('courseA') && !rule.courseA) return;
    if (fields.includes('courseB') && !rule.courseB) return;
    if (fields.includes('courses') && (!rule.courses || rule.courses.length < 2)) return;
    if (fields.includes('daysOfWeek') && (!rule.daysOfWeek || rule.daysOfWeek.length === 0)) return;
    if (newRule.type === RULE_TYPES.LOCATION_BLACKOUT && !rule.location) return;
    if (newRule.type === RULE_TYPES.LOCATION_REASSIGN && (!rule.fromLocation || !rule.toLocation)) return;

    if (persistSchedulingRules([...planningRules.schedulingRules, rule])) {
      setNewRule(emptyRuleForm);
    }
  }, [newRule, persistSchedulingRules, planningRules.schedulingRules]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleRemoveRule = useCallback((id) => {
    persistRules(prev => ({ ...prev, schedulingRules: prev.schedulingRules.filter(r => r.id !== id) }));
  }, [persistRules]);

  const handleToggleRuleEnabled = useCallback((id) => {
    persistSchedulingRules(planningRules.schedulingRules.map(r =>
      r.id === id ? { ...r, enabled: r.enabled === false } : r
    ));
  }, [persistSchedulingRules, planningRules.schedulingRules]);

  const handleToggleRuleSeverity = useCallback((id) => {
    persistSchedulingRules(planningRules.schedulingRules.map(r =>
      r.id === id ? { ...r, severity: r.severity === 'soft' ? 'hard' : 'soft' } : r
    ));
  }, [persistSchedulingRules, planningRules.schedulingRules]);

  // Ask the AI to assist with planning, given the current rules + analysis.
  const requestPlanningAdvice = useCallback(async () => {
    if (!analysisData || courseProfiles.length === 0) return;
    setPlanningLoading(true);
    setPlanningAdvice('');
    try {
      const structured = summarizeRulesForAI([
        ...BUILT_IN_SCHEDULING_RULES,
        ...planningRules.schedulingRules
      ]);
      const rulesText = [
        `Excluded courses (never scheduled): ${[...DEFAULT_EXCLUDE_KEYWORDS, ...planningRules.excludeKeywords].join(', ')}`,
        ...(structured ? ['Structured scheduling rules (enforced):', structured] : []),
        ...(planningRules.planningNotes.length
          ? ['Manager planning notes:', ...planningRules.planningNotes.map((n, i) => `${i + 1}. ${n.text}`)]
          : [])
      ].join('\n');
      const result = await aiScheduleService.reviewPlanningRules(draftSessions, rulesText, targetYear);
      setPlanningAdvice(result.success ? result.data : `AI planning assistant unavailable: ${result.error}`);
    } catch (err) {
      setPlanningAdvice(`Error: ${err.message}`);
    } finally {
      setPlanningLoading(false);
    }
  }, [analysisData, courseProfiles, draftSessions, targetYear, planningRules]);

  // --- Draft Management ---
  // Track the currently-loaded draft so subsequent saves UPDATE the same record
  // instead of creating a new draft each time. Cleared when user starts fresh.
  const [currentDraftId, setCurrentDraftId] = useState(null);
  const [currentDraftName, setCurrentDraftName] = useState('');

  const persistDraft = useCallback(async ({ id, name, silent = false } = {}) => {
    if (draftSessions.length === 0) {
      if (!silent) alert('Nothing to save — generate or add sessions first.');
      return null;
    }
    const now = new Date().toISOString();
    const isUpdate = !!id;
    const draft = {
      id: id || `draft-${targetYear}-${Date.now()}`,
      name: name || currentDraftName || `${targetYear} Schedule Draft`,
      targetYear,
      sessions: draftSessions,
      conflicts,
      createdBy: account?.name || 'Unknown',
      createdAt: isUpdate ? undefined : now, // saveDraft stamps updatedAt; preserve existing createdAt on update
      aiSuggestions,
      managerFeedback,
      feedbackResponse,
      planningRulesSnapshot: planningRules,
      planningRulesSnapshotVersion: 1
    };
    try {
      await saveDraft(draft);
    } catch (err) {
      console.error('Save draft failed:', err);
      if (!silent) alert(`Failed to save draft: ${err.message || err}\n\nIf this persists, check that AZURE_STORAGE_CONNECTION_STRING is configured in the Static Web App settings and you are signed in as an authorized Schedule Manager.`);
      return null;
    }
    setCurrentDraftId(draft.id);
    setCurrentDraftName(draft.name);
    try {
      const all = await getAllDrafts();
      setSavedDrafts(all);
    } catch (err) {
      console.warn('Save succeeded but list refresh failed:', err);
    }
    if (!silent) alert(isUpdate ? `Draft "${draft.name}" updated.` : `Draft "${draft.name}" saved.`);
    return draft.id;
  }, [draftSessions, conflicts, targetYear, account, aiSuggestions, managerFeedback, feedbackResponse, planningRules, currentDraftName]);

  // Save (updates current draft if loaded, else creates new) — used by toolbar button
  const handleSaveDraft = useCallback(async () => {
    if (currentDraftId) {
      await persistDraft({ id: currentDraftId, name: currentDraftName });
    } else {
      const name = window.prompt(
        'Name this draft so you can find it later:',
        `${targetYear} Schedule Draft`
      );
      if (name === null) return; // user cancelled
      await persistDraft({ name: name.trim() || `${targetYear} Schedule Draft` });
    }
  }, [currentDraftId, currentDraftName, persistDraft, targetYear]);

  // Save As New — fork a copy under a new id (useful for "save as a snapshot")
  const handleSaveDraftAsNew = useCallback(async () => {
    const name = window.prompt(
      'Save a new copy of this draft. Name it:',
      currentDraftName ? `${currentDraftName} (copy)` : `${targetYear} Schedule Draft`
    );
    if (name === null) return;
    await persistDraft({ name: name.trim() || `${targetYear} Schedule Draft` });
  }, [currentDraftName, targetYear, persistDraft]);

  const handleLoadDrafts = useCallback(async () => {
    const all = await getAllDrafts();
    setSavedDrafts(all);
  }, []);

  // Preload saved drafts on mount so the Drafts tab badge is accurate and
  // users can find their previous work right away.
  useEffect(() => {
    handleLoadDrafts();
  }, [handleLoadDrafts]);

  // Load the shared, org-wide planning rules from the server on mount so every
  // schedule manager sees the same rules across devices and future runs. The
  // localStorage cache provides the instant initial state; this overwrites it
  // with the authoritative shared set once it arrives.
  useEffect(() => {
    refreshSharedPlanningRules();
  }, [refreshSharedPlanningRules]);

  useEffect(() => {
    setRuleCourseNames((planningRules.courseProfileOptions || []).map(option => option.name).filter(Boolean));
  }, [planningRules.courseProfileOptions]);

  const handleRestoreDraft = useCallback(async (summary) => {
    const draft = await getDraft(summary.id);
    if (!draft) return;
    setDraftSessions(draft.sessions);
    setConflicts(draft.conflicts || []);
    setTargetYear(draft.targetYear);
    setAiSuggestions(draft.aiSuggestions || '');
    setManagerFeedback(draft.managerFeedback || '');
    setFeedbackResponse(draft.feedbackResponse || '');
    if (draft.planningRulesSnapshot) {
      const restoredRules = normalizePlanningRules(draft.planningRulesSnapshot);
      rulesScopeRef.current = 'draft';
      setPlanningRules(restoredRules);
      setRuleCourseNames((restoredRules.courseProfileOptions || []).map(option => option.name).filter(Boolean));
      setRulesSyncStatus('draft');
    } else {
      rulesScopeRef.current = 'shared';
      setPlanningRules(sharedPlanningRulesRef.current);
      setRuleCourseNames((sharedPlanningRulesRef.current.courseProfileOptions || []).map(option => option.name).filter(Boolean));
      setRulesSyncStatus('saved');
    }
    setCurrentDraftId(draft.id);
    setCurrentDraftName(draft.name || `${draft.targetYear} Schedule Draft`);
    setActiveTab('calendar'); // jump straight to the working surface
  }, []);

  // Start a new blank draft (clears current binding so next save creates a new id)
  const handleNewDraft = useCallback(() => {
    if (draftSessions.length > 0 && !window.confirm('Clear the current draft and start fresh? Unsaved changes will be lost.')) return;
    setDraftSessions([]);
    setConflicts([]);
    setCurrentDraftId(null);
    setCurrentDraftName('');
    rulesScopeRef.current = 'shared';
    setPlanningRules(sharedPlanningRulesRef.current);
    setRuleCourseNames((sharedPlanningRulesRef.current.courseProfileOptions || []).map(option => option.name).filter(Boolean));
    setRulesSyncStatus('saved');
  }, [draftSessions.length]);

  const handleDeleteDraft = useCallback(async (id) => {
    await deleteDraft(id);
    const all = await getAllDrafts();
    setSavedDrafts(all);
  }, []);

  const handleExportDraft = useCallback(async (id) => {
    const draft = await getDraft(id);
    if (!draft) return;

    const sessions = Array.isArray(draft.sessions) ? draft.sessions : [];

    // Build a flat row per session for Excel
    const rows = sessions.map(s => ({
      'Session Name': s.name || '',
      'Course': s.course || s.courseName || '',
      'Department': s.department || '',
      'Start Date': s.startDate ? String(s.startDate).slice(0, 10) : '',
      'End Date': s.endDate ? String(s.endDate).slice(0, 10) : '',
      'Location': s.location || '',
      'Classroom': s.classroom || '',
      'Campus': s.campus || '',
      'Max Capacity': s.maxCapacity ?? '',
      'Instructor': s.instructor || '',
      'Approval Status': s.approvalStatus || 'pending',
      'Notes': s.notes || ''
    }));

    const headers = [
      'Session Name', 'Course', 'Department', 'Start Date', 'End Date',
      'Location', 'Classroom', 'Campus', 'Max Capacity', 'Instructor',
      'Approval Status', 'Notes'
    ];

    const sheet = XLSX.utils.json_to_sheet(rows, { header: headers });

    // Auto-size columns based on header + content length
    sheet['!cols'] = headers.map(h => {
      const maxLen = Math.max(
        h.length,
        ...rows.map(r => String(r[h] ?? '').length)
      );
      return { wch: Math.min(Math.max(maxLen + 2, 10), 40) };
    });

    // Metadata sheet for context
    const meta = [
      ['Draft Name', draft.name || ''],
      ['Target Year', draft.targetYear ?? ''],
      ['Created', draft.createdAt || ''],
      ['Last Updated', draft.updatedAt || ''],
      ['Created By', draft.createdBy || ''],
      ['Last Edited By', draft.lastEditedBy || ''],
      ['Total Sessions', sessions.length]
    ];
    const metaSheet = XLSX.utils.aoa_to_sheet(meta);
    metaSheet['!cols'] = [{ wch: 18 }, { wch: 50 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, 'Schedule');
    XLSX.utils.book_append_sheet(wb, metaSheet, 'Info');

    const safeName = (draft.name || `schedule-${targetYear}`).replace(/[^\w-]+/g, '_');
    XLSX.writeFile(wb, `${safeName}-${draft.targetYear || targetYear}.xlsx`);
  }, [targetYear]);

  // Export the projected-revenue-by-week report for a draft. Uses the SAME
  // draft sessions (and therefore the same scheduling rules) as the draft
  // itself; revenue = projected seats taken x per-seat course cost.
  const handleExportRevenue = useCallback(async (id) => {
    setRevenueExportingId(id);
    try {
      const draft = await getDraft(id);
      if (!draft) return;

      const reportYear = Number(draft.targetYear) || new Date().getFullYear() + 1;
      const sessions = Array.isArray(draft.sessions) ? draft.sessions : [];

    // Pull current course list prices directly from Dataverse (mi_course).
    let priceInfo = { success: false, priceField: null, byGuid: {}, byName: {}, count: 0 };
    try {
      priceInfo = await scheduleGeneratorService.getCoursePrices();
    } catch (err) {
      console.error('Failed to load course prices from Dataverse:', err);
    }
    const getCourseCost = createPriceResolver(priceInfo, () => null);

    // Prior-period actuals: real historical sessions (with seats taken) so each
    // week can show last year's actual registrations next to this year's plan.
    let priorSessions = [];
    try {
      const histResult = await scheduleGeneratorService.getHistoricalSessions(3);
      if (histResult?.success && Array.isArray(histResult.data)) priorSessions = histResult.data;
    } catch (err) {
      console.error('Failed to load historical sessions for prior-period actuals:', err);
    }

    const report = buildRevenueReport(sessions, { getCourseCost, year: reportYear });
    const { aoa, merges, colWidths, autofilterRef, currencyCols } = revenueReportToWorkbookData(report);

    // Monthly, by-hub sanity check: planned registrations (this year) vs actual
    // registrations in the same month last year, so the CEO can spot over/under
    // estimation. Monthly + hub buckets absorb week/city/room schedule moves.
    const reportYearSessions = sessions.filter(s => Number(String(s?.startDate || '').slice(0, 4)) === reportYear);
    const hubReport = buildHubSanityReport(reportYearSessions, { priorSessions, priorYearOffset: 1 });
    const hubWb = hubSanityToWorkbookData(hubReport);

    const sheet = XLSX.utils.aoa_to_sheet(aoa);
    sheet['!merges'] = merges;
    sheet['!cols'] = colWidths;
    // Sort/filter dropdowns on every column for the CEO.
    if (autofilterRef) sheet['!autofilter'] = { ref: XLSX.utils.encode_range(autofilterRef) };

    // Number-format cells: currency for revenue columns, plain integers for the
    // registration-count columns (planned + prior-period actuals).
    const currencySet = new Set(currencyCols || []);
    const range = XLSX.utils.decode_range(sheet['!ref']);
    for (let r = range.s.r; r <= range.e.r; r++) {
      for (let c = range.s.c; c <= range.e.c; c++) {
        const cell = sheet[XLSX.utils.encode_cell({ r, c })];
        if (cell && cell.t === 'n') cell.z = currencySet.has(c) ? '"$"#,##0' : '#,##0';
      }
    }

    // Assumptions sheet — transparency for approval.
    const seatModeLabel = report.seatMode === 'capacity'
      ? 'Full max capacity (every seat sold)'
      : 'Max capacity × historical average fill rate';
    const priceSource = priceInfo.success
      ? `Dataverse mi_course.${priceInfo.priceField} (${priceInfo.count} courses priced)`
      : `Dataverse mi_course unavailable; costs left blank${priceInfo.error ? ` (${priceInfo.error})` : ''}`;
    const assumptions = [
      ['Projected Revenue Report — Assumptions'],
      [],
      ['Draft Name', draft.name || ''],
      ['Target Year', reportYear],
      ['Included dates', `${reportYear} only`],
      ['Generated', new Date().toLocaleString('en-GB')],
      ['Projected seats basis', seatModeLabel],
      ['Revenue formula', 'Projected seats taken × per-seat course list price'],
      ['Course price source', priceSource],
      ['Bundle child sessions', 'Excluded (only sellable parent/standalone courses earn)'],
      [],
      ['"Projected Revenue" sheet — each week shows two columns:'],
      ['  Revenue ($)', 'Projected seats taken × per-seat course list price'],
      ['  Planned Reg', 'Planned registrations this period (projected seats, same basis as revenue)'],
      [],
      ['"Sanity Check (by Hub)" sheet:'],
      ['  Buckets', 'Monthly, rolled up to the four course hubs (WA / HI / CA / VA)'],
      ['  Purpose', 'Planned registrations (this year) vs actual registrations same month last year, to spot over/under estimation'],
      ['  Planned', 'Projected seats taken (same basis as revenue)'],
      [`  PY-${hubReport.priorYearOffset || 1} Actual`, `Actual seats taken on the calendar in the same month ${hubReport.priorYearOffset || 1} year(s) earlier, matched by course + hub`],
      ['  Variance', 'Planned − Prior-year Actual (positive = planning above last year, negative = below)'],
      ['  Prior-year source', priorSessions.length ? `Dataverse historical sessions (${priorSessions.length} loaded)` : 'Dataverse unavailable — prior-year actuals shown as 0'],
      ['  Hub source', 'INTERIM: derived from the session city/campus in src/utils/hubLocation.js until the hub is a field in Power Apps'],
      [],
      ['Locations that could NOT be placed in a hub (shown under "Unclassified" — add cues to src/utils/hubLocation.js):'],
      ...(hubReport.unclassified.length === 0
        ? [['(none — every location mapped to a hub)']]
        : hubReport.unclassified.map(name => [name])),
      [],
      ['Courses with NO price found (left blank and valued at $0 — verify in mi_course):']
    ];
    if (report.missingCosts.length === 0) {
      assumptions.push(['(none — every course has a price)']);
    } else {
      report.missingCosts.forEach(name => assumptions.push([name]));
    }
    const assumptionsSheet = XLSX.utils.aoa_to_sheet(assumptions);
    assumptionsSheet['!cols'] = [{ wch: 34 }, { wch: 70 }];

    // Sanity-check sheet: integer counts, with Variance columns highlighting
    // negatives in red so under-planning is obvious at a glance.
    const hubSheet = XLSX.utils.aoa_to_sheet(hubWb.aoa);
    hubSheet['!merges'] = hubWb.merges;
    hubSheet['!cols'] = hubWb.colWidths;
    if (hubWb.autofilterRef) hubSheet['!autofilter'] = { ref: XLSX.utils.encode_range(hubWb.autofilterRef) };
    const varianceSet = new Set(hubWb.varianceCols || []);
    const hubRange = XLSX.utils.decode_range(hubSheet['!ref']);
    for (let r = hubRange.s.r; r <= hubRange.e.r; r++) {
      for (let c = hubRange.s.c; c <= hubRange.e.c; c++) {
        const cell = hubSheet[XLSX.utils.encode_cell({ r, c })];
        if (cell && cell.t === 'n') cell.z = varianceSet.has(c) ? '#,##0;[Red]-#,##0' : '#,##0';
      }
    }

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, sheet, 'Projected Revenue');
    XLSX.utils.book_append_sheet(wb, hubSheet, 'Sanity Check (by Hub)');
    XLSX.utils.book_append_sheet(wb, assumptionsSheet, 'Assumptions');

      const safeName = (draft.name || `revenue-${reportYear}`).replace(/[^\w-]+/g, '_');
      XLSX.writeFile(wb, `${safeName}-projected-revenue-${reportYear}.xlsx`);
    } catch (err) {
      console.error('Failed to build projected revenue report:', err);
      setError(`Revenue report failed: ${err.message}`);
    } finally {
      setRevenueExportingId(null);
    }
  }, []);

  const handleImportDraft = useCallback(async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const text = await file.text();
    const draft = await importDraftFromJSON(text);
    if (draft) {
      handleRestoreDraft(draft);
      const all = await getAllDrafts();
      setSavedDrafts(all);
    }
  }, [handleRestoreDraft]);

  // --- Publishing ---
  // eslint-disable-next-line no-unused-vars
  const handlePublish = useCallback(async () => {
    if (!canPublish) {
      alert('You do not have permission to publish schedules. Please ask a Schedule Admin to publish on your behalf.');
      return;
    }
    if (PUSH_TO_PA_DISABLED) {
      alert('Publishing to Power Apps is temporarily disabled while the new course filter and seed data are being reconciled. No records were sent.');
      return;
    }
    if (!window.confirm(`Publish ${draftSessions.length} sessions to Dataverse for ${targetYear}? This will create real session records.`)) return;

    setPublishing(true);
    try {
      const results = await scheduleGeneratorService.publishDraftSessions(draftSessions);
      setPublishResults(results);
    } catch (err) {
      setPublishResults({ created: 0, failed: draftSessions.length, errors: [{ session: 'All', error: err.message }] });
    } finally {
      setPublishing(false);
    }
  }, [draftSessions, targetYear, canPublish]);

  // --- Draft Editing ---
  const handleRemoveSession = useCallback((sessionId) => {
    setDraftSessions(prev => {
      const updated = prev.filter(s => s.id !== sessionId);
      setConflicts(detectConflicts(updated));
      return updated;
    });
  }, []);

  const handleUpdateSession = useCallback((sessionId, field, value) => {
    setDraftSessions(prev => {
      const updated = prev.map(s => s.id === sessionId ? { ...s, [field]: value } : s);
      setConflicts(detectConflicts(updated));
      return updated;
    });
  }, []);

  const handleAddSession = useCallback(() => {
    if (!newSession.name.trim()) return;
    const session = {
      id: `manual-${Date.now()}`,
      name: newSession.name.trim(),
      startDate: newSession.startDate + 'T09:00:00.000Z',
      endDate: newSession.endDate + 'T17:00:00.000Z',
      maxCapacity: parseInt(newSession.maxCapacity) || 20,
      locationGuid: newSession.locationGuid || null,
      locationName: newSession.locationName || '',
      department: newSession.department || '',
      departmentName: newSession.department || '',
      classroom: newSession.classroom || '',
      classroomName: newSession.classroom || '',
      campus: newSession.campus || '',
      campusName: newSession.campus || '',
      instructor: newSession.instructor || '',
      mi_instructor: newSession.instructor || '',
      isDraft: true,
      confidence: 0,
      basedOnSessions: 0,
      avgFillRate: 0,
      trend: 'stable',
      suggestedBy: 'manual',
      approvalStatus: 'pending',
      managerNotes: '',
      aiSuggestion: ''
    };
    setDraftSessions(prev => {
      const updated = [...prev, session];
      setConflicts(detectConflicts(updated));
      return updated;
    });
    setNewSession({
      name: '',
      ...getDefaultSessionDates(targetYear),
      maxCapacity: 20,
      instructor: '',
      department: '',
      locationName: '',
      locationGuid: null,
      classroom: '',
      campus: ''
    });
    setShowAddForm(false);
  }, [newSession, targetYear]);

  // --- Approval ---
  const handleApproveSession = useCallback((sessionId) => {
    setDraftSessions(prev => prev.map(s => s.id === sessionId ? { ...s, approvalStatus: 'approved' } : s));
  }, []);

  const handleUnapproveSession = useCallback((sessionId) => {
    setDraftSessions(prev => prev.map(s => s.id === sessionId ? { ...s, approvalStatus: 'pending' } : s));
  }, []);

  const handleBatchApprove = useCallback((sessionIds) => {
    setDraftSessions(prev => prev.map(s => sessionIds.includes(s.id) ? { ...s, approvalStatus: 'approved' } : s));
  }, []);

  const handleBatchUnapprove = useCallback((sessionIds) => {
    setDraftSessions(prev => prev.map(s => sessionIds.includes(s.id) ? { ...s, approvalStatus: 'pending' } : s));
  }, []);

  const handlePublishApproved = useCallback(async () => {
    if (!canPublish) {
      alert('You do not have permission to publish schedules. Please ask a Schedule Admin to publish on your behalf.');
      return;
    }
    if (PUSH_TO_PA_DISABLED) {
      alert('Publishing to Power Apps is temporarily disabled while the new course filter and seed data are being reconciled. No records were sent.');
      return;
    }
    const approved = draftSessions.filter(s => s.approvalStatus === 'approved');
    if (approved.length === 0) return;
    if (!window.confirm(`Publish ${approved.length} approved session${approved.length > 1 ? 's' : ''} to Dataverse? This will create real session records.`)) return;

    setPublishing(true);
    setPublishResults(null);
    const ids = new Set(approved.map(s => s.id));
    setPublishingSessionIds(ids);
    try {
      const results = await scheduleGeneratorService.publishDraftSessions(approved);
      setPublishResults(results);
      if (results.created > 0) {
        setDraftSessions(prev => prev.map(s => ids.has(s.id) && !results.errors?.find(e => e.session === s.name) ? { ...s, approvalStatus: 'published' } : s));
      }
    } catch (err) {
      setPublishResults({ created: 0, failed: approved.length, errors: [{ session: 'All', error: err.message }] });
    } finally {
      setPublishing(false);
      setPublishingSessionIds(new Set());
    }
  }, [draftSessions, canPublish]);

  const handlePublishSingle = useCallback(async (session) => {
    if (!canPublish) {
      alert('You do not have permission to publish schedules. Please ask a Schedule Admin to publish on your behalf.');
      return;
    }
    if (PUSH_TO_PA_DISABLED) {
      alert('Publishing to Power Apps is temporarily disabled while the new course filter and seed data are being reconciled. No records were sent.');
      return;
    }
    if (!window.confirm(`Publish "${session.name}" to Dataverse?`)) return;

    setPublishingSessionIds(new Set([session.id]));
    try {
      const results = await scheduleGeneratorService.publishDraftSessions([session]);
      if (results.created > 0) {
        setDraftSessions(prev => prev.map(s => s.id === session.id ? { ...s, approvalStatus: 'published' } : s));
      } else {
        alert(`Failed to publish: ${results.errors?.[0]?.error || 'Unknown error'}`);
      }
    } catch (err) {
      alert(`Failed to publish: ${err.message}`);
    } finally {
      setPublishingSessionIds(new Set());
    }
  }, [canPublish]);

  // --- Computed values ---
  const analyticsSummary = useMemo(() => {
    if (!analysisData || courseProfiles.length === 0) return null;
    return generateAnalyticsSummary(courseProfiles, analysisData.sessions, { excludeKeywords: planningRules.excludeKeywords });
  }, [analysisData, courseProfiles, planningRules.excludeKeywords]);

  // Course Profiles after applying the search filter and the chosen sort.
  const displayedProfiles = useMemo(() => {
    const q = profileSearch.trim().toLowerCase();
    const list = q
      ? courseProfiles.filter(c => (c.name || '').toLowerCase().includes(q))
      : courseProfiles.slice();
    const trendRank = { increasing: 3, stable: 2, decreasing: 1 };
    const valueOf = (c) => {
      switch (profileSortField) {
        case 'totalSessions': return c.totalSessions || 0;
        case 'avgFrequencyPerYear': return c.avgFrequencyPerYear || 0;
        case 'typicalCapacity': return c.typicalCapacity || 0;
        case 'typicalDuration': return c.typicalDuration || 0;
        case 'trend': return trendRank[c.trend] || 0;
        case 'name':
        default: return (c.name || '').toLowerCase();
      }
    };
    list.sort((a, b) => {
      const va = valueOf(a);
      const vb = valueOf(b);
      const cmp = (typeof va === 'string' || typeof vb === 'string')
        ? String(va).localeCompare(String(vb))
        : va - vb;
      return profileSortDir === 'asc' ? cmp : -cmp;
    });
    return list;
  }, [courseProfiles, profileSearch, profileSortField, profileSortDir]);

  // How many courses are currently switched ON for draft generation.
  const includedCount = useMemo(() => {
    const excluded = new Set(planningRules.excludedCourseKeys || []);
    return courseProfiles.filter(c => !excluded.has(c.key)).length;
  }, [courseProfiles, planningRules.excludedCourseKeys]);

  // Sort-direction indicator for the active column header.
  const sortArrow = (field) =>
    profileSortField === field ? (profileSortDir === 'asc' ? ' \u25B2' : ' \u25BC') : '';

  // Course names pulled from the DB (historical sessions → course profiles),
  // used to populate the rule-builder autocomplete dropdowns.
  const courseNameOptions = useMemo(() => {
    // Never union the current profiles with a shared snapshot: doing so can
    // resurrect courses removed by a newer analysis. Current profiles win;
    // the shared snapshot is only a cross-browser fallback before analysis.
    const names = courseProfiles.length > 0
      ? includedProfileOptions(courseProfiles).map(option => option.name)
      : ruleCourseNames;
    return [...new Set(names.filter(Boolean))].sort((a, b) => a.localeCompare(b));
  }, [courseProfiles, includedProfileOptions, ruleCourseNames]);
  // Location names from the DB, for the rule-builder location dropdown.
  const locationNameOptions = useMemo(
    () => [...new Set([...(ruleLocations || []), ...(analysisData?.locations || [])].map(l => l.name).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [analysisData, ruleLocations]
  );
  // Which input fields the currently-selected rule type needs.
  const activeRuleFields = RULE_TYPE_META[newRule.type]?.fields || [];
  const canAddRule =
    (!activeRuleFields.includes('courseA') || newRule.courseA.trim()) &&
    (!activeRuleFields.includes('courseB') || newRule.courseB.trim()) &&
    (!activeRuleFields.includes('courses') || newRule.courses.length >= 2) &&
    (!activeRuleFields.includes('daysOfWeek') || newRule.daysOfWeek.length > 0) &&
    (newRule.type !== RULE_TYPES.LOCATION_BLACKOUT || newRule.location.trim()) &&
    (newRule.type !== RULE_TYPES.LOCATION_REASSIGN || (newRule.fromLocation.trim() && newRule.toLocation.trim()));

  const filteredDraft = useMemo(() => {
    let filtered = draftSessions;
    if (selectedMonth !== 'all') {
      const month = parseInt(selectedMonth);
      filtered = filtered.filter(s => new Date(s.startDate).getMonth() === month);
    }
    if (searchFilter) {
      const term = searchFilter.toLowerCase();
      filtered = filtered.filter(s => s.name.toLowerCase().includes(term));
    }
    return filtered;
  }, [draftSessions, selectedMonth, searchFilter]);

  const draftByMonth = useMemo(() => {
    const grouped = {};
    draftSessions.forEach(s => {
      const month = new Date(s.startDate).getMonth();
      if (!grouped[month]) grouped[month] = [];
      grouped[month].push(s);
    });
    return grouped;
  }, [draftSessions]);

  const approvalCounts = useMemo(() => {
    const pending = draftSessions.filter(s => !s.approvalStatus || s.approvalStatus === 'pending').length;
    const approved = draftSessions.filter(s => s.approvalStatus === 'approved').length;
    const published = draftSessions.filter(s => s.approvalStatus === 'published').length;
    return { pending, approved, published };
  }, [draftSessions]);

  // --- Access Control ---
  if (roleChecking) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ fontSize: '16px', color: '#6b7280' }}>Checking permissions...</p>
      </div>
    );
  }

  if (!isScheduleManager) {
    return (
      <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '32px', maxWidth: '480px', textAlign: 'center' }}>
          <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#991b1b', marginBottom: '8px' }}>Access Denied</h2>
          <p style={{ color: '#dc2626' }}>You do not have schedule manager permissions to access this tool.</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#f9fafb' }}>
      {/* Header */}
      <header style={{ backgroundColor: '#1e293b', borderBottom: '3px solid #8b5cf6', padding: '16px 32px' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 style={{ fontSize: '24px', fontWeight: '700', color: '#ffffff', margin: 0 }}>Schedule Generator</h1>
            <p style={{ fontSize: '14px', color: '#94a3b8', margin: '4px 0 0' }}>AI-Assisted Draft Schedule for {targetYear}</p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
            <select
              value={targetYear}
              onChange={(e) => setTargetYear(Number(e.target.value))}
              style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #475569', backgroundColor: '#334155', color: '#fff', fontSize: '14px' }}
            >
              <option value={2026}>2026</option>
              <option value={2027}>2027</option>
              <option value={2028}>2028</option>
            </select>
            <div style={{ textAlign: 'right' }}>
              <p style={{ fontSize: '14px', color: '#94a3b8', margin: 0 }}>Signed in as</p>
              <p style={{ fontSize: '14px', fontWeight: '500', color: '#ffffff', margin: 0 }}>{account?.name}</p>
            </div>
          </div>
        </div>
      </header>

      {/* Tab Navigation */}
      <div style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e5e7eb' }}>
        <div style={{ maxWidth: '1400px', margin: '0 auto', display: 'flex', gap: '0' }}>
          {[
            { id: 'analyze', label: '📊 Analyze', badge: courseProfiles.length > 0 ? courseProfiles.length : null },
            { id: 'draft', label: '📅 Draft Schedule', badge: draftSessions.length > 0 ? draftSessions.length : null },
            { id: 'calendar', label: '🗓️ Calendar View', badge: draftSessions.length > 0 ? draftSessions.length : null },
            ...(HIDE_AI_TAB ? [] : [{ id: 'ai', label: '🤖 AI Suggestions', badge: aiSuggestions ? '✓' : null }]),
            { id: 'rules', label: '🧭 Planning Rules', badge: (planningRules.excludeKeywords.length + planningRules.planningNotes.length) || null },
            { id: 'drafts', label: '💾 Saved Drafts', badge: savedDrafts.length > 0 ? savedDrafts.length : null }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => { setActiveTab(tab.id); if (tab.id === 'drafts') handleLoadDrafts(); }}
              style={{
                padding: '12px 24px',
                border: 'none',
                borderBottom: activeTab === tab.id ? '3px solid #8b5cf6' : '3px solid transparent',
                backgroundColor: 'transparent',
                color: activeTab === tab.id ? '#8b5cf6' : '#6b7280',
                fontWeight: activeTab === tab.id ? '600' : '400',
                fontSize: '14px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '6px'
              }}
            >
              {tab.label}
              {tab.badge && (
                <span style={{
                  backgroundColor: activeTab === tab.id ? '#8b5cf6' : '#e5e7eb',
                  color: activeTab === tab.id ? '#fff' : '#374151',
                  borderRadius: '10px',
                  padding: '1px 8px',
                  fontSize: '12px',
                  fontWeight: '600'
                }}>
                  {tab.badge}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      <main style={{ maxWidth: '1400px', margin: '0 auto', padding: '24px 32px' }}>
        {generationError && (
          <div role="alert" style={{ backgroundColor: '#fff7ed', border: '1px solid #fdba74', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
            <h2 style={{ color: '#9a3412', fontSize: '16px', fontWeight: '600', margin: '0 0 8px' }}>Draft generation needs correction</h2>
            <p style={{ color: '#9a3412', margin: '0 0 14px', whiteSpace: 'pre-wrap' }}>{generationError}</p>
            <button
              type="button"
              onClick={() => setActiveTab('rules')}
              style={{ ...btnSecondary, color: '#9a3412', borderColor: '#fdba74', backgroundColor: '#ffffff' }}
            >
              Review Planning Rules
            </button>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div style={{ backgroundColor: '#fef2f2', border: '1px solid #fecaca', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
            <p style={{ color: '#dc2626', margin: 0 }}>{error}</p>
          </div>
        )}

        {/* Loading State */}
        {loading && (
          <div style={{ textAlign: 'center', padding: '48px' }}>
            <Spinner size={48} />
            <div style={{ fontSize: '16px', color: '#6b7280', margin: '16px 0 8px' }}>Loading analysis data...</div>
            <div style={{ fontSize: '14px', color: '#8b5cf6' }}>{loadingPhase || 'Fetching sessions from Dataverse...'}</div>
          </div>
        )}

        {/* ANALYZE TAB */}
        {activeTab === 'analyze' && !loading && (
          <div>
            {!analysisData ? (
              <div style={{ textAlign: 'center', padding: '64px' }}>
                <h2 style={{ fontSize: '20px', color: '#1f2937', marginBottom: '8px' }}>Historical Schedule Analysis</h2>
                <p style={{ color: '#6b7280', marginBottom: '24px' }}>
                  Analyze 3 years of session data to identify patterns, demand, and optimal scheduling.
                </p>
                <button
                  onClick={() => loadAnalysis(false)}
                  style={{ padding: '12px 32px', backgroundColor: '#8b5cf6', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '16px', fontWeight: '600', cursor: 'pointer' }}
                >
                  Load Analysis Data
                </button>
              </div>
            ) : (
              <>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap', backgroundColor: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px 14px', marginBottom: '16px' }}>
                  <span style={{ color: '#475569', fontSize: '12px' }}>
                    Data as of <strong>{analysisData.analysisCache?.cachedAt ? new Date(analysisData.analysisCache.cachedAt).toLocaleString() : 'this session'}</strong>
                    {analysisData.analysisCache?.source === 'browser-cache' && ' · loaded from the 8-hour browser cache'}
                  </span>
                  <button type="button" onClick={() => loadAnalysis(true)} style={{ ...btnSecondary, fontSize: '12px' }}>
                    Refresh from Dataverse
                  </button>
                </div>

                {/* Summary Cards */}
                {analyticsSummary && (
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px', marginBottom: '24px' }}>
                    <SummaryCard label="Unique Courses" value={analyticsSummary.totalCourses} color="#8b5cf6" />
                    <SummaryCard label="Total Sessions" value={analyticsSummary.totalHistoricalSessions} color="#3b82f6" />
                    <SummaryCard label="Years Analyzed" value={analyticsSummary.yearRange} color="#10b981" />
                    <SummaryCard label="Avg Fill Rate" value={`${Math.round(analyticsSummary.avgFillRate * 100)}%`} color="#f59e0b" />
                  </div>
                )}

                {/* Utilization Breakdown */}
                {analyticsSummary && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
                    {/* Monthly Volume */}
                    <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '20px' }}>
                      <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginTop: 0, marginBottom: '16px' }}>Monthly Session Volume</h3>
                      <div style={{ display: 'flex', alignItems: 'flex-end', gap: '4px', height: '120px' }}>
                        {analyticsSummary.monthlyVolume.map((count, i) => {
                          const maxVol = Math.max(...analyticsSummary.monthlyVolume, 1);
                          return (
                            <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                              <span style={{ fontSize: '10px', color: '#6b7280', marginBottom: '4px' }}>{count}</span>
                              <div style={{
                                width: '100%',
                                height: `${(count / maxVol) * 100}px`,
                                backgroundColor: '#8b5cf6',
                                borderRadius: '2px 2px 0 0',
                                minHeight: count > 0 ? '4px' : '0'
                              }} />
                              <span style={{ fontSize: '10px', color: '#6b7280', marginTop: '4px' }}>{MONTH_NAMES[i]}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>

                    {/* Utilization */}
                    <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '20px' }}>
                      <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginTop: 0, marginBottom: '16px' }}>Utilization Breakdown</h3>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                        <UtilBar label="Under-utilized (<50%)" count={analyticsSummary.utilizationBreakdown.underUtilized} total={analyticsSummary.totalCourses} color="#ef4444" />
                        <UtilBar label="Well-utilized (50-90%)" count={analyticsSummary.utilizationBreakdown.wellUtilized} total={analyticsSummary.totalCourses} color="#22c55e" />
                        <UtilBar label="Over-subscribed (>90%)" count={analyticsSummary.utilizationBreakdown.overSubscribed} total={analyticsSummary.totalCourses} color="#f59e0b" />
                      </div>
                    </div>
                  </div>
                )}

                {/* Course Profiles Table */}
                <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '20px', marginBottom: '24px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '16px', gap: '16px' }}>
                    <div>
                      <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: 0 }}>
                        Course Profiles ({courseProfiles.length}) &middot; <span style={{ color: '#16a34a' }}>{includedCount} included</span>
                      </h3>
                      <p style={{ fontSize: '12px', color: '#6b7280', margin: '4px 0 0' }}>
                        One row per course (grouped by the Dataverse course, not session title).
                        <strong> Sessions</strong> = total historical sessions analyzed; <strong>Freq/Year</strong> = average sessions per year.
                        Click a column header to sort. Untick <strong>Include</strong> to keep a course out of the generated draft (saved automatically).
                      </p>
                    </div>
                    <button
                      onClick={generateDraft}
                      disabled={isGenerating || courseProfiles.length === 0}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px',
                        padding: '8px 20px',
                        backgroundColor: isGenerating ? '#a78bfa' : '#8b5cf6',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: isGenerating ? 'wait' : 'pointer'
                      }}
                    >
                      {isGenerating && (
                        <>
                          <style>{'@keyframes sg-spin { to { transform: rotate(360deg); } }'}</style>
                          <span
                            aria-hidden="true"
                            style={{
                              width: 14,
                              height: 14,
                              borderRadius: '50%',
                              border: '2px solid rgba(255,255,255,0.4)',
                              borderTopColor: '#fff',
                              animation: 'sg-spin 0.8s linear infinite'
                            }}
                          />
                        </>
                      )}
                      {isGenerating ? `Generating ${targetYear} draft…` : `Generate ${targetYear} Draft →`}
                    </button>
                  </div>
                  {/* Search + bulk include/exclude toolbar */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px', flexWrap: 'wrap' }}>
                    <input
                      type="text"
                      value={profileSearch}
                      onChange={(e) => setProfileSearch(e.target.value)}
                      placeholder="Filter courses by name..."
                      style={{ flex: '1 1 240px', minWidth: '200px', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                    />
                    {profileSearch && (
                      <button onClick={() => setProfileSearch('')} style={{ ...btnSecondary }}>Clear</button>
                    )}
                    <span style={{ fontSize: '13px', color: '#6b7280' }}>{displayedProfiles.length} shown</span>
                    <div style={{ flex: 1 }} />
                    <button
                      onClick={() => setCoursesExcluded(displayedProfiles.map(c => c.key), false)}
                      style={{ ...btnSecondary, color: '#16a34a', borderColor: '#bbf7d0' }}
                    >
                      Include shown
                    </button>
                    <button
                      onClick={() => setCoursesExcluded(displayedProfiles.map(c => c.key), true)}
                      style={{ ...btnSecondary, color: '#b91c1c', borderColor: '#fecaca' }}
                    >
                      Exclude shown
                    </button>
                  </div>

                  <div style={{ maxHeight: '500px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead style={{ position: 'sticky', top: 0, backgroundColor: '#ffffff' }}>
                        <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                          <th style={{ ...thStyle, textAlign: 'center', width: '64px' }}>Include</th>
                          <th style={{ ...thStyle, cursor: 'pointer', userSelect: 'none' }} onClick={() => handleProfileSort('name')}>Course Name{sortArrow('name')}</th>
                          <th style={{ ...thStyle, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleProfileSort('totalSessions')}>Sessions{sortArrow('totalSessions')}</th>
                          <th style={{ ...thStyle, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleProfileSort('avgFrequencyPerYear')}>Freq/Year{sortArrow('avgFrequencyPerYear')}</th>
                          <th style={{ ...thStyle, textAlign: 'center' }}>Suggested</th>
                          <th style={{ ...thStyle, textAlign: 'center' }}>Avg Fill</th>
                          <th style={{ ...thStyle, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleProfileSort('typicalCapacity')}>Capacity{sortArrow('typicalCapacity')}</th>
                          <th style={{ ...thStyle, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleProfileSort('typicalDuration')}>Duration{sortArrow('typicalDuration')}</th>
                          <th style={{ ...thStyle, textAlign: 'center', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleProfileSort('trend')}>Trend{sortArrow('trend')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {displayedProfiles.map(course => {
                          const isExcluded = (planningRules.excludedCourseKeys || []).includes(course.key);
                          return (
                            <tr key={course.key} style={{ borderBottom: '1px solid #f3f4f6', opacity: isExcluded ? 0.45 : 1, backgroundColor: isExcluded ? '#fafafa' : 'transparent' }}>
                              <td style={{ ...tdStyle, textAlign: 'center' }}>
                                <input
                                  type="checkbox"
                                  checked={!isExcluded}
                                  onChange={() => toggleCourseIncluded(course.key)}
                                  title={isExcluded ? 'Excluded from draft - click to include' : 'Included in draft - click to exclude'}
                                  style={{ cursor: 'pointer', width: '16px', height: '16px' }}
                                />
                              </td>
                              <td style={{ ...tdStyle, fontWeight: '500', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: isExcluded ? 'line-through' : 'none' }}>{course.name}</td>
                              <td style={{ ...tdStyle, textAlign: 'center' }}>{course.totalSessions}</td>
                              <td style={{ ...tdStyle, textAlign: 'center' }}>{course.avgFrequencyPerYear}</td>
                              <td style={{ ...tdStyle, textAlign: 'center', fontWeight: '600', color: '#8b5cf6' }}>{course.suggestedFrequency}</td>
                              <td style={{ ...tdStyle, textAlign: 'center' }}>
                                <span style={{ color: course.avgFillRate >= 0.7 ? '#22c55e' : course.avgFillRate >= 0.4 ? '#f59e0b' : '#ef4444' }}>
                                  {Math.round(course.avgFillRate * 100)}%
                                </span>
                              </td>
                              <td style={{ ...tdStyle, textAlign: 'center' }}>{Math.round(course.typicalCapacity)}</td>
                              <td style={{ ...tdStyle, textAlign: 'center' }}>{course.typicalDuration}d</td>
                              <td style={{ ...tdStyle, textAlign: 'center' }}>
                                <TrendBadge trend={course.trend} />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* DRAFT TAB */}
        {activeTab === 'draft' && (
          <div>
            {draftSessions.length === 0 && !showAddForm ? (
              <div style={{ textAlign: 'center', padding: '64px', color: '#6b7280' }}>
                <p>No draft generated yet. Go to the Analyze tab and click "Generate Draft".</p>
                <p style={{ marginTop: '8px' }}>Or <button onClick={() => setShowAddForm(true)} style={{ color: '#8b5cf6', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontSize: 'inherit' }}>add a session manually</button>.</p>
              </div>
            ) : (
              <>
                {/* Approval Summary Bar */}
                <div style={{ display: 'flex', gap: '16px', marginBottom: '16px', backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#d1d5db' }} />
                    <span style={{ fontSize: '14px', color: '#6b7280' }}>Pending: <strong style={{ color: '#374151' }}>{approvalCounts.pending}</strong></span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#22c55e' }} />
                    <span style={{ fontSize: '14px', color: '#6b7280' }}>Approved: <strong style={{ color: '#166534' }}>{approvalCounts.approved}</strong></span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#3b82f6' }} />
                    <span style={{ fontSize: '14px', color: '#6b7280' }}>Published: <strong style={{ color: '#1d4ed8' }}>{approvalCounts.published}</strong></span>
                  </div>
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => handleBatchApprove(filteredDraft.filter(s => !s.approvalStatus || s.approvalStatus === 'pending').map(s => s.id))}
                      disabled={filteredDraft.every(s => s.approvalStatus === 'approved' || s.approvalStatus === 'published')}
                      style={{ ...btnSecondary, color: '#166534', borderColor: '#bbf7d0', backgroundColor: '#f0fdf4', cursor: filteredDraft.every(s => s.approvalStatus === 'approved' || s.approvalStatus === 'published') ? 'not-allowed' : 'pointer', opacity: filteredDraft.every(s => s.approvalStatus === 'approved' || s.approvalStatus === 'published') ? 0.5 : 1 }}
                    >
                      ✓ Approve All Visible
                    </button>
                    <button
                      onClick={() => handleBatchUnapprove(filteredDraft.filter(s => s.approvalStatus === 'approved').map(s => s.id))}
                      disabled={!filteredDraft.some(s => s.approvalStatus === 'approved')}
                      style={{ ...btnSecondary, opacity: !filteredDraft.some(s => s.approvalStatus === 'approved') ? 0.5 : 1, cursor: !filteredDraft.some(s => s.approvalStatus === 'approved') ? 'not-allowed' : 'pointer' }}
                    >
                      Unapprove All Visible
                    </button>
                    <button
                      onClick={handlePublishApproved}
                      disabled={PUSH_TO_PA_DISABLED || publishing || approvalCounts.approved === 0}
                      title={PUSH_TO_PA_DISABLED ? 'Publishing to Power Apps is temporarily disabled (new course filter pending reconciliation)' : ''}
                      style={{ ...btnPrimary, backgroundColor: '#9ca3af', cursor: 'not-allowed' }}
                    >
                      {PUSH_TO_PA_DISABLED ? '🔒 Publishing Disabled' : (publishing ? 'Publishing...' : `🚀 Publish ${approvalCounts.approved} Approved`)}
                    </button>
                  </div>
                </div>

                {/* Draft Controls */}
                <div style={{ display: 'flex', gap: '12px', marginBottom: '24px', flexWrap: 'wrap', alignItems: 'center' }}>
                  <select
                    value={selectedMonth}
                    onChange={(e) => setSelectedMonth(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px' }}
                  >
                    <option value="all">All Months</option>
                    {MONTH_FULL.map((name, i) => (
                      <option key={i} value={i}>{name} ({draftByMonth[i]?.length || 0})</option>
                    ))}
                  </select>
                  <input
                    type="text"
                    placeholder="Search courses..."
                    value={searchFilter}
                    onChange={(e) => setSearchFilter(e.target.value)}
                    style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', width: '200px' }}
                  />
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                    <button onClick={() => setShowAddForm(!showAddForm)} style={{ ...btnSecondary, color: '#8b5cf6', borderColor: '#c4b5fd' }}>
                      {showAddForm ? '✕ Cancel' : '+ Add Session'}
                    </button>
                    <button onClick={handleSaveDraft} style={btnSecondary}>💾 Save Draft</button>
                  </div>
                </div>

                {/* Add Session Form */}
                {showAddForm && (
                  <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '2px solid #c4b5fd', padding: '20px', marginBottom: '16px' }}>
                    <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: '0 0 12px' }}>Add New Session</h4>
                    <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                      <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', flex: '1 1 200px' }}>
                        Course Name
                        <input
                          type="text"
                          value={newSession.name}
                          onChange={(e) => setNewSession(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="e.g. STCW Basic Safety Training"
                          style={{ display: 'block', width: '100%', marginTop: '4px', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                        />
                      </label>
                      <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>
                        Start Date
                        <input
                          type="date"
                          value={newSession.startDate}
                          onChange={(e) => setNewSession(prev => ({ ...prev, startDate: e.target.value }))}
                          style={{ display: 'block', marginTop: '4px', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        />
                      </label>
                      <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>
                        End Date
                        <input
                          type="date"
                          value={newSession.endDate}
                          onChange={(e) => setNewSession(prev => ({ ...prev, endDate: e.target.value }))}
                          style={{ display: 'block', marginTop: '4px', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        />
                      </label>
                      <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>
                        Capacity
                        <input
                          type="number"
                          value={newSession.maxCapacity}
                          onChange={(e) => setNewSession(prev => ({ ...prev, maxCapacity: e.target.value }))}
                          style={{ display: 'block', marginTop: '4px', width: '80px', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                        />
                      </label>
                      <button
                        onClick={handleAddSession}
                        disabled={!newSession.name.trim()}
                        style={{ ...btnPrimary, backgroundColor: !newSession.name.trim() ? '#9ca3af' : '#8b5cf6', cursor: !newSession.name.trim() ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}
                      >
                        Add to Draft
                      </button>
                    </div>
                  </div>
                )}

                {/* Conflict Warnings */}
                {conflicts.length > 0 && (
                  <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', padding: '16px', marginBottom: '16px' }}>
                    <h4 style={{ margin: '0 0 8px', color: '#92400e', fontSize: '14px' }}>⚠️ {conflicts.length} Scheduling Conflict{conflicts.length > 1 ? 's' : ''}</h4>
                    {conflicts.slice(0, 5).map((c, i) => (
                      <p key={i} style={{ margin: '4px 0', fontSize: '13px', color: '#78350f' }}>
                        {c.message}
                      </p>
                    ))}
                    {conflicts.length > 5 && (
                      <p style={{ margin: '4px 0', fontSize: '13px', color: '#92400e', fontStyle: 'italic' }}>
                        ...and {conflicts.length - 5} more
                      </p>
                    )}
                  </div>
                )}

                {/* Publish Results */}
                {publishResults && (
                  <div style={{
                    backgroundColor: publishResults.failed > 0 ? '#fef2f2' : '#f0fdf4',
                    border: `1px solid ${publishResults.failed > 0 ? '#fecaca' : '#bbf7d0'}`,
                    borderRadius: '8px', padding: '16px', marginBottom: '16px'
                  }}>
                    <p style={{ margin: 0, fontWeight: '600', color: publishResults.failed > 0 ? '#991b1b' : '#166534' }}>
                      Published: {publishResults.created} created, {publishResults.failed} failed
                    </p>
                    {publishResults.errors?.slice(0, 3).map((err, i) => (
                      <p key={i} style={{ margin: '4px 0 0', fontSize: '13px', color: '#991b1b' }}>{err.session}: {err.error}</p>
                    ))}
                  </div>
                )}

                {/* Monthly Overview Bar */}
                <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '16px', marginBottom: '16px' }}>
                  <div style={{ display: 'flex', gap: '2px' }}>
                    {MONTH_NAMES.map((name, i) => {
                      const count = draftByMonth[i]?.length || 0;
                      const hasConflict = conflicts.some(c => {
                        const conflictMonth = new Date(c.date).getMonth();
                        return conflictMonth === i;
                      });
                      return (
                        <div
                          key={i}
                          onClick={() => setSelectedMonth(count > 0 ? String(i) : 'all')}
                          style={{
                            flex: 1,
                            textAlign: 'center',
                            padding: '8px 4px',
                            borderRadius: '4px',
                            cursor: count > 0 ? 'pointer' : 'default',
                            backgroundColor: selectedMonth === String(i) ? '#8b5cf6' : count > 0 ? '#f3e8ff' : '#f9fafb',
                            color: selectedMonth === String(i) ? '#fff' : '#374151',
                            border: hasConflict ? '2px solid #f59e0b' : '1px solid transparent'
                          }}
                        >
                          <div style={{ fontSize: '11px', fontWeight: '500' }}>{name}</div>
                          <div style={{ fontSize: '16px', fontWeight: '700' }}>{count}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                {/* Draft Sessions Table */}
                <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '20px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginTop: 0, marginBottom: '16px' }}>
                    Draft Sessions ({filteredDraft.length} of {draftSessions.length})
                  </h3>
                  <div style={{ maxHeight: '600px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead style={{ position: 'sticky', top: 0, backgroundColor: '#ffffff' }}>
                        <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                          <th style={{ ...thStyle, textAlign: 'center', width: '50px' }}>Status</th>
                          <th style={thStyle}>Course</th>
                          <th style={{ ...thStyle, textAlign: 'center' }}>Start Date</th>
                          <th style={{ ...thStyle, textAlign: 'center' }}>End Date</th>
                          <th style={{ ...thStyle, textAlign: 'center' }}>Capacity</th>
                          <th style={{ ...thStyle, textAlign: 'center' }}>Fill Rate</th>
                          <th style={{ ...thStyle, textAlign: 'center' }}>Confidence</th>
                          <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {filteredDraft.map(session => {
                          const status = session.approvalStatus || 'pending';
                          const isPublishing = publishingSessionIds.has(session.id);
                          return (
                          <tr key={session.id} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: status === 'published' ? '#f0f9ff' : status === 'approved' ? '#f0fdf4' : 'transparent' }}>
                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                              {status === 'published' ? (
                                <span style={{ color: '#3b82f6', fontSize: '16px' }} title="Published to Dataverse">✔</span>
                              ) : isPublishing ? (
                                <span style={{ color: '#6b7280', fontSize: '12px' }}>⏳</span>
                              ) : (
                                <input
                                  type="checkbox"
                                  checked={status === 'approved'}
                                  onChange={() => status === 'approved' ? handleUnapproveSession(session.id) : handleApproveSession(session.id)}
                                  style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#22c55e' }}
                                  title={status === 'approved' ? 'Click to unapprove' : 'Click to approve'}
                                />
                              )}
                            </td>
                            <td style={{ ...tdStyle, fontWeight: '500', maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                              {session.name}
                              {session.suggestedBy === 'ai' && <span style={{ marginLeft: '4px', fontSize: '11px' }}>🤖</span>}
                              {session.suggestedBy === 'manual' && <span style={{ marginLeft: '4px', fontSize: '11px' }}>✏️</span>}
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                              <input
                                type="date"
                                value={session.startDate.split('T')[0]}
                                onChange={(e) => handleUpdateSession(session.id, 'startDate', e.target.value + 'T09:00:00.000Z')}
                                style={{ border: '1px solid #e5e7eb', borderRadius: '4px', padding: '2px 6px', fontSize: '13px' }}
                              />
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                              <input
                                type="date"
                                value={session.endDate.split('T')[0]}
                                onChange={(e) => handleUpdateSession(session.id, 'endDate', e.target.value + 'T17:00:00.000Z')}
                                style={{ border: '1px solid #e5e7eb', borderRadius: '4px', padding: '2px 6px', fontSize: '13px' }}
                              />
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                              <input
                                type="number"
                                value={session.maxCapacity}
                                onChange={(e) => handleUpdateSession(session.id, 'maxCapacity', parseInt(e.target.value) || 0)}
                                style={{ width: '60px', border: '1px solid #e5e7eb', borderRadius: '4px', padding: '2px 6px', fontSize: '13px', textAlign: 'center' }}
                              />
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                              <span style={{ color: session.avgFillRate >= 0.7 ? '#22c55e' : session.avgFillRate >= 0.4 ? '#f59e0b' : '#ef4444' }}>
                                {Math.round((session.avgFillRate || 0) * 100)}%
                              </span>
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                              <ConfidenceBadge value={session.confidence} />
                            </td>
                            <td style={{ ...tdStyle, textAlign: 'center' }}>
                              <div style={{ display: 'flex', gap: '4px', justifyContent: 'center' }}>
                                {status !== 'published' && (
                                  <button
                                    onClick={() => status === 'approved' ? handlePublishSingle(session) : handleApproveSession(session.id)}
                                    disabled={isPublishing || (PUSH_TO_PA_DISABLED && status === 'approved')}
                                    style={{ border: 'none', background: 'none', cursor: (isPublishing || (PUSH_TO_PA_DISABLED && status === 'approved')) ? 'not-allowed' : 'pointer', color: (PUSH_TO_PA_DISABLED && status === 'approved') ? '#9ca3af' : (status === 'approved' ? '#22c55e' : '#8b5cf6'), fontSize: '13px', fontWeight: '500' }}
                                    title={PUSH_TO_PA_DISABLED && status === 'approved' ? 'Publishing to Power Apps is temporarily disabled' : (status === 'approved' ? 'Publish to Dataverse' : 'Approve session')}
                                  >
                                    {isPublishing ? '⏳' : (PUSH_TO_PA_DISABLED && status === 'approved') ? '🔒' : status === 'approved' ? '🚀' : '✓'}
                                  </button>
                                )}
                                {status !== 'published' && (
                                  <button
                                    onClick={() => handleRemoveSession(session.id)}
                                    style={{ border: 'none', background: 'none', cursor: 'pointer', color: '#ef4444', fontSize: '16px' }}
                                    title="Remove session"
                                  >
                                    ✕
                                  </button>
                                )}
                              </div>
                            </td>
                          </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        {/* CALENDAR VIEW TAB */}
        {activeTab === 'calendar' && (
          <div>
            {draftSessions.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '64px', color: '#6b7280' }}>
                <p>No draft generated yet. Go to the Analyze tab and click "Generate Draft".</p>
                <p style={{ marginTop: '8px' }}>Or <button onClick={() => setShowAddForm(true)} style={{ color: '#8b5cf6', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline', fontSize: 'inherit' }}>add a session manually</button>.</p>
              </div>
            ) : (
              <DraftCalendarView
                draftSessions={draftSessions}
                calendarDate={calendarDate}
                setCalendarDate={setCalendarDate}
                targetYear={targetYear}
                conflicts={conflicts}
                selectedSession={calendarSelectedSession}
                setSelectedSession={setCalendarSelectedSession}
                onRemoveSession={handleRemoveSession}
                onUpdateSession={handleUpdateSession}
                onApproveSession={handleApproveSession}
                onUnapproveSession={handleUnapproveSession}
                onPublishSingle={handlePublishSingle}
                publishingSessionIds={publishingSessionIds}
                approvalCounts={approvalCounts}
                onBatchApprove={handleBatchApprove}
                onPublishApproved={handlePublishApproved}
                publishing={publishing}
                showAddForm={showAddForm}
                setShowAddForm={setShowAddForm}
                newSession={newSession}
                setNewSession={setNewSession}
                onAddSession={handleAddSession}
                onSaveDraft={handleSaveDraft}
                onSaveDraftAsNew={handleSaveDraftAsNew}
                onNewDraft={handleNewDraft}
                currentDraftName={currentDraftName}
                currentDraftId={currentDraftId}
                analysisData={analysisData}
              />
            )}
          </div>
        )}

        {/* AI TAB */}
        {!HIDE_AI_TAB && activeTab === 'ai' && (
          <div>
            {!aiScheduleService.isConfigured && (
              <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', padding: '16px', marginBottom: '24px' }}>
                <h4 style={{ margin: '0 0 8px', color: '#92400e', fontSize: '14px' }}>AI Not Configured</h4>
                <p style={{ margin: 0, fontSize: '13px', color: '#78350f' }}>
                  Set <code>REACT_APP_AI_ENDPOINT</code> and <code>REACT_APP_AI_API_KEY</code> in your environment to enable AI suggestions.
                  Supports Azure OpenAI (set <code>REACT_APP_AI_IS_AZURE=true</code>) or OpenAI directly.
                </p>
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px' }}>
              {/* AI Suggestions Panel */}
              <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '20px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                  <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: 0 }}>🤖 AI Analysis & Suggestions</h3>
                  <button
                    onClick={requestAISuggestions}
                    disabled={aiLoading || !analysisData}
                    style={{
                      ...btnPrimary,
                      backgroundColor: aiLoading || !analysisData ? '#9ca3af' : '#8b5cf6',
                      cursor: aiLoading || !analysisData ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {aiLoading ? 'Analyzing...' : 'Get Suggestions'}
                  </button>
                </div>
                {!analysisData && (
                  <p style={{ color: '#6b7280', fontSize: '14px' }}>Load analysis data first from the Analyze tab.</p>
                )}
                {aiSuggestions && (
                  <div style={{ maxHeight: '500px', overflowY: 'auto', fontSize: '14px', lineHeight: '1.6', color: '#374151' }}>
                    <MarkdownContent content={aiSuggestions} />
                  </div>
                )}
              </div>

              {/* Manager Feedback Panel */}
              <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '20px' }}>
                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', margin: '0 0 16px' }}>💬 Manager Feedback</h3>
                <p style={{ fontSize: '13px', color: '#6b7280', marginBottom: '12px' }}>
                  Describe changes or adjustments you'd like to make to the draft schedule. AI will suggest specific modifications.
                </p>
                <textarea
                  value={managerFeedback}
                  onChange={(e) => setManagerFeedback(e.target.value)}
                  placeholder={`e.g. "We need more STCW courses in Q1 due to new regulations. ECDIS courses should be reduced to quarterly. Move all safety courses away from July-August holiday period."`}
                  style={{
                    width: '100%',
                    minHeight: '120px',
                    padding: '12px',
                    borderRadius: '6px',
                    border: '1px solid #d1d5db',
                    fontSize: '14px',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
                <button
                  onClick={submitFeedback}
                  disabled={aiLoading || !managerFeedback.trim() || draftSessions.length === 0}
                  style={{
                    ...btnPrimary,
                    marginTop: '12px',
                    backgroundColor: aiLoading || !managerFeedback.trim() ? '#9ca3af' : '#8b5cf6',
                    cursor: aiLoading || !managerFeedback.trim() ? 'not-allowed' : 'pointer'
                  }}
                >
                  {aiLoading ? 'Processing...' : 'Submit Feedback'}
                </button>
                {feedbackResponse && (
                  <div style={{ marginTop: '16px', maxHeight: '300px', overflowY: 'auto', fontSize: '14px', lineHeight: '1.6', color: '#374151' }}>
                    <MarkdownContent content={feedbackResponse} />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* PLANNING RULES TAB */}
        {activeTab === 'rules' && (
          <div>
            <div style={{ marginBottom: '20px' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', margin: '0 0 4px' }}>🧭 Planning Rules</h3>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ fontSize: '12px', fontWeight: '600', color: rulesSyncStatus === 'error' || rulesSyncStatus === 'conflict' ? '#b91c1c' : rulesSyncStatus === 'saved' ? '#047857' : '#6d28d9' }}>
                    {rulesSyncStatus === 'saving' && 'Saving shared rules…'}
                    {rulesSyncStatus === 'loading' && 'Loading shared rules…'}
                    {rulesSyncStatus === 'saved' && '✓ Shared rules saved'}
                    {rulesSyncStatus === 'draft' && '✓ Rules saved with this draft'}
                    {rulesSyncStatus === 'conflict' && '⚠ Shared rules changed — refresh before editing'}
                    {rulesSyncStatus === 'error' && '⚠ Not shared — using this browser only'}
                  </span>
                  <button
                    type="button"
                    onClick={rulesSyncStatus === 'error' ? () => syncPlanningRules(planningRules) : () => refreshSharedPlanningRules(rulesSyncStatus === 'draft')}
                    disabled={rulesSyncStatus === 'saving' || rulesSyncStatus === 'loading'}
                    style={{ ...btnSecondary, padding: '4px 9px', fontSize: '11px' }}
                  >
                    {rulesSyncStatus === 'error' ? 'Retry save' : rulesSyncStatus === 'draft' ? 'Use shared rules' : 'Refresh shared rules'}
                  </button>
                </div>
              </div>
              <p style={{ fontSize: '13px', color: '#6b7280', margin: 0 }}>
                Define scheduling rules once and let the generator apply them automatically — no need to edit individual sessions.
                Exclusion rules drop matching courses from the schedule; planning notes guide the AI assistant.
              </p>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', backgroundColor: '#eff6ff', borderRadius: '8px', border: '1px solid #bfdbfe', padding: '14px 18px', marginBottom: '24px' }}>
              <div style={{ flex: '1 1 560px' }}>
                <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1e3a8a', margin: '0 0 6px' }}>Built-in teaching-day rules</h4>
                <p style={{ fontSize: '12px', color: '#1e40af', margin: 0, lineHeight: '1.6' }}>
                  Weekends and holidays are not teaching days. Two- and three-day courses use a consecutive block within one Monday–Friday week; four-day courses run Monday–Thursday or Tuesday–Friday; five-day courses run a complete holiday-free Monday–Friday. Longer courses resume on the next instructional weekday. The same course is kept out of the same calendar week across San Diego, Everett, and Norfolk whenever possible; if no other hard-compliant week remains, the draft flags a primary-location separation exception for review.
                </p>
              </div>
              <button
                type="button"
                onClick={generateDraft}
                disabled={isGenerating}
                style={{ ...btnPrimary, backgroundColor: isGenerating ? '#9ca3af' : '#2563eb', cursor: isGenerating ? 'wait' : 'pointer', whiteSpace: 'nowrap' }}
              >
                {isGenerating ? 'Generating…' : `${courseProfiles.length === 0 ? 'Load data & generate' : 'Generate'} ${targetYear} Draft`}
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              {/* Exclusions panel */}
              <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '20px' }}>
                <h4 style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937', margin: '0 0 4px' }}>🚫 Excluded Courses</h4>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 14px' }}>
                  Any matching session is never scheduled. Changes rebuild the course profiles immediately from the analysis data already loaded in this browser.
                </p>

                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '14px' }}>
                  {DEFAULT_EXCLUDE_KEYWORDS.map(kw => (
                    <span key={kw} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: '#f3f4f6', color: '#374151', borderRadius: '14px', padding: '4px 10px', fontSize: '13px', fontWeight: '500' }}>
                      {kw}
                      <span title="Built-in rule (always on)" style={{ fontSize: '11px', color: '#9ca3af' }}>🔒</span>
                    </span>
                  ))}
                  {planningRules.excludeKeywords.map(kw => (
                    <span key={kw} style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', backgroundColor: '#fef2f2', color: '#b91c1c', borderRadius: '14px', padding: '4px 10px', fontSize: '13px', fontWeight: '500' }}>
                      {kw}
                      <button onClick={() => handleRemoveExcludeKeyword(kw)} title="Remove" style={{ border: 'none', background: 'none', color: '#b91c1c', cursor: 'pointer', fontSize: '14px', lineHeight: 1, padding: 0 }}>×</button>
                    </span>
                  ))}
                </div>

                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={newExcludeKeyword}
                    onChange={(e) => setNewExcludeKeyword(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddExcludeKeyword(); }}
                    placeholder="e.g. Webinar"
                    style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box' }}
                  />
                  <button onClick={handleAddExcludeKeyword} disabled={!newExcludeKeyword.trim()} style={{ ...btnSecondary, color: '#b91c1c', borderColor: '#fecaca', cursor: newExcludeKeyword.trim() ? 'pointer' : 'not-allowed', opacity: newExcludeKeyword.trim() ? 1 : 0.5 }}>+ Add</button>
                </div>
              </div>

              {/* Planning notes panel */}
              <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '20px' }}>
                <h4 style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937', margin: '0 0 4px' }}>📝 Planning Notes</h4>
                <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 14px' }}>
                  Free-text notes are advisory context only. Use the deterministic scheduling tools below for behavior that must be enforced.
                </p>

                {planningRules.planningNotes.length === 0 ? (
                  <p style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic', margin: '0 0 14px' }}>No planning rules yet.</p>
                ) : (
                  <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 14px' }}>
                    {planningRules.planningNotes.map(note => (
                      <li key={note.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', padding: '8px 10px', borderRadius: '6px', backgroundColor: '#f9fafb', marginBottom: '6px', fontSize: '13px', color: '#374151' }}>
                        <span>{note.text}</span>
                        <button onClick={() => handleRemovePlanningNote(note.id)} title="Remove" style={{ border: 'none', background: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '16px', lineHeight: 1, padding: 0 }}>×</button>
                      </li>
                    ))}
                  </ul>
                )}

                <div style={{ display: 'flex', gap: '8px' }}>
                  <input
                    type="text"
                    value={newPlanningNote}
                    onChange={(e) => setNewPlanningNote(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleAddPlanningNote(); }}
                    placeholder='e.g. "Schedule PSC after Able Seaman; never the same week"'
                    style={{ flex: 1, padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', boxSizing: 'border-box' }}
                  />
                  <button onClick={handleAddPlanningNote} disabled={!newPlanningNote.trim()} style={{ ...btnSecondary, color: '#8b5cf6', borderColor: '#c4b5fd', cursor: newPlanningNote.trim() ? 'pointer' : 'not-allowed', opacity: newPlanningNote.trim() ? 1 : 0.5 }}>+ Add</button>
                </div>
              </div>
            </div>

            {/* Structured scheduling rules (enforced) */}
            <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '20px', marginBottom: '24px' }}>
              <h4 style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937', margin: '0 0 4px' }}>📐 Scheduling Rules (enforced)</h4>
              <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 16px' }}>
                Load the included Course Profile list and active locations, then set your rules and generate a new draft. Rule changes do not require another analysis download.
              </p>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px', flexWrap: 'wrap', padding: '16px', marginBottom: '16px', borderRadius: '8px', backgroundColor: '#f5f3ff', border: '2px solid #c4b5fd' }}>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '700', color: '#5b21b6' }}>Step 1: Load rule dropdown options</div>
                  <div style={{ fontSize: '12px', color: '#6d28d9', marginTop: '3px' }}>
                    {ruleOptionsLoaded
                      ? `${courseNameOptions.length} courses and ${locationNameOptions.length} locations loaded from Dataverse.`
                      : 'Required before selecting courses and locations for a rule.'}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={loadRuleOptions}
                  disabled={ruleOptionsLoading}
                  style={{
                    padding: '11px 20px',
                    border: 'none',
                    borderRadius: '7px',
                    backgroundColor: ruleOptionsLoading ? '#a78bfa' : '#7c3aed',
                    color: '#ffffff',
                    fontSize: '14px',
                    fontWeight: '700',
                    boxShadow: '0 2px 5px rgba(124, 58, 237, 0.25)',
                    cursor: ruleOptionsLoading ? 'wait' : 'pointer'
                  }}
                >
                  {ruleOptionsLoading ? 'Loading from Dataverse…' : ruleOptionsLoaded ? '↻ Refresh Course & Location Lists' : 'Load Course & Location Lists'}
                </button>
              </div>

              <div style={{ marginBottom: '16px', padding: '10px', borderRadius: '6px', backgroundColor: '#ecfdf5', border: '1px solid #a7f3d0', fontSize: '13px', color: '#065f46' }}>
                <strong>Built-in:</strong> {describeRule(BUILT_IN_SCHEDULING_RULES[0])} 🔒
              </div>

              {/* Existing rules */}
              {planningRules.schedulingRules.length === 0 ? (
                <p style={{ fontSize: '13px', color: '#9ca3af', fontStyle: 'italic', margin: '0 0 16px' }}>No scheduling rules yet.</p>
              ) : (
                <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 16px' }}>
                  {planningRules.schedulingRules.map(rule => (
                    <li key={rule.id} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 10px', borderRadius: '6px', backgroundColor: rule.enabled === false ? '#f9fafb' : '#f5f3ff', marginBottom: '6px', fontSize: '13px', color: '#374151', opacity: rule.enabled === false ? 0.55 : 1 }}>
                      <span style={{ flex: 1 }}>{describeRule(rule)}</span>
                      {[RULE_TYPES.LOCATION_BLACKOUT, RULE_TYPES.LOCATION_REASSIGN].includes(rule.type) ? (
                        <span style={{ ...btnSecondary, padding: '2px 10px', fontSize: '11px', color: '#b91c1c', borderColor: '#fecaca', backgroundColor: '#fef2f2' }}>HARD</span>
                      ) : (
                        <button
                          onClick={() => handleToggleRuleSeverity(rule.id)}
                          title="Toggle hard / soft"
                          style={{ ...btnSecondary, padding: '2px 10px', fontSize: '11px', color: rule.severity === 'soft' ? '#92400e' : '#b91c1c', borderColor: rule.severity === 'soft' ? '#fcd34d' : '#fecaca', backgroundColor: rule.severity === 'soft' ? '#fffbeb' : '#fef2f2' }}
                        >
                          {rule.severity === 'soft' ? 'SOFT' : 'HARD'}
                        </button>
                      )}
                      <button onClick={() => handleToggleRuleEnabled(rule.id)} title={rule.enabled === false ? 'Enable' : 'Disable'} style={{ ...btnSecondary, padding: '2px 10px', fontSize: '11px' }}>
                        {rule.enabled === false ? 'Off' : 'On'}
                      </button>
                      <button onClick={() => handleRemoveRule(rule.id)} title="Remove" style={{ border: 'none', background: 'none', color: '#9ca3af', cursor: 'pointer', fontSize: '16px', lineHeight: 1, padding: 0 }}>×</button>
                    </li>
                  ))}
                </ul>
              )}

              {/* Rule builder */}
              <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px' }}>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'flex-end' }}>
                  <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: '#6b7280' }}>
                    Rule type
                    <select
                      value={newRule.type}
                      onChange={(e) => setNewRule({ ...emptyRuleForm, type: e.target.value, severity: RULE_TYPE_META[e.target.value]?.defaultSeverity || 'hard' })}
                      style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', minWidth: '230px' }}
                    >
                      {Object.entries(RULE_TYPE_META).map(([type, meta]) => (
                        <option key={type} value={type}>{meta.label}</option>
                      ))}
                    </select>
                  </label>

                  {activeRuleFields.includes('courseA') && (
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: '#6b7280' }}>
                      {newRule.type === RULE_TYPES.SEQUENCE ? 'Course A (first)' : 'Course'}
                      <select value={newRule.courseA} onChange={(e) => setNewRule({ ...newRule, courseA: e.target.value })} style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', minWidth: '240px' }}>
                        <option value="">Select a course</option>
                        {courseNameOptions.map(course => <option key={course} value={course}>{course}</option>)}
                      </select>
                    </label>
                  )}
                  {activeRuleFields.includes('courseB') && (
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: '#6b7280' }}>
                      {newRule.type === RULE_TYPES.SEQUENCE ? 'Course B (after A)' : 'Second course'}
                      <select value={newRule.courseB} onChange={(e) => setNewRule({ ...newRule, courseB: e.target.value })} style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', minWidth: '240px' }}>
                        <option value="">Select a course</option>
                        {courseNameOptions.map(course => <option key={course} value={course}>{course}</option>)}
                      </select>
                    </label>
                  )}
                  {activeRuleFields.includes('courses') && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: '#6b7280', minWidth: '320px' }}>
                      <span>{newRule.type === RULE_TYPES.ORDERED_GROUP ? 'Courses in order' : 'Courses'}</span>
                      <div style={{ display: 'flex', gap: '6px' }}>
                        <select value={newRule.coursePicker} onChange={(e) => setNewRule({ ...newRule, coursePicker: e.target.value })} style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', flex: 1 }}>
                          <option value="">Select a course</option>
                          {courseNameOptions.filter(course => !newRule.courses.includes(course)).map(course => <option key={course} value={course}>{course}</option>)}
                        </select>
                        <button type="button" disabled={!newRule.coursePicker.trim() || newRule.courses.includes(newRule.coursePicker.trim())} onClick={() => setNewRule({ ...newRule, courses: [...newRule.courses, newRule.coursePicker.trim()], coursePicker: '' })} style={{ ...btnSecondary, padding: '7px 10px' }}>Add course</button>
                      </div>
                      {newRule.courses.map((course, index) => (
                        <div key={`${course}-${index}`} style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#374151' }}>
                          <strong>{index + 1}.</strong><span style={{ flex: 1 }}>{course}</span>
                          {index > 0 && <button type="button" title="Move up" onClick={() => { const courses = [...newRule.courses]; [courses[index - 1], courses[index]] = [courses[index], courses[index - 1]]; setNewRule({ ...newRule, courses }); }} style={{ border: 'none', background: 'none', cursor: 'pointer' }}>↑</button>}
                          {index < newRule.courses.length - 1 && <button type="button" title="Move down" onClick={() => { const courses = [...newRule.courses]; [courses[index], courses[index + 1]] = [courses[index + 1], courses[index]]; setNewRule({ ...newRule, courses }); }} style={{ border: 'none', background: 'none', cursor: 'pointer' }}>↓</button>}
                          <button type="button" title="Remove" onClick={() => setNewRule({ ...newRule, courses: newRule.courses.filter((_, i) => i !== index) })} style={{ border: 'none', background: 'none', color: '#b91c1c', cursor: 'pointer' }}>×</button>
                        </div>
                      ))}
                    </div>
                  )}
                  {activeRuleFields.includes('daysOfWeek') && (
                    <fieldset style={{ display: 'flex', gap: '8px', border: 0, padding: 0, margin: 0, alignItems: 'center' }}>
                      <legend style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>Allowed start days</legend>
                      {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((day, index) => (
                        <label key={day} style={{ fontSize: '12px', color: '#374151', display: 'flex', gap: '3px', alignItems: 'center' }}>
                          <input type="checkbox" checked={newRule.daysOfWeek.includes(index)} onChange={() => setNewRule({ ...newRule, daysOfWeek: newRule.daysOfWeek.includes(index) ? newRule.daysOfWeek.filter(value => value !== index) : [...newRule.daysOfWeek, index].sort() })} />
                          {day}
                        </label>
                      ))}
                    </fieldset>
                  )}
                  {activeRuleFields.includes('gapDays') && (
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: '#6b7280' }}>
                      {newRule.type === RULE_TYPES.ORDERED_GROUP ? 'Max instructional days for whole group' : 'Min days apart'}
                      <input type="number" min="1" value={newRule.gapDays} onChange={(e) => setNewRule({ ...newRule, gapDays: e.target.value })} style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', width: '110px' }} />
                    </label>
                  )}
                  {activeRuleFields.includes('location') && (
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: '#6b7280' }}>
                      {newRule.type === RULE_TYPES.LOCATION_BLACKOUT ? 'Location' : 'Location (optional)'}
                      <select value={newRule.location} onChange={(e) => setNewRule({ ...newRule, location: e.target.value })} style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', minWidth: '200px' }}>
                        <option value="">{newRule.type === RULE_TYPES.LOCATION_BLACKOUT ? 'Select a location' : 'All locations'}</option>
                        {locationNameOptions.map(location => <option key={location} value={location}>{location}</option>)}
                      </select>
                    </label>
                  )}
                  {activeRuleFields.includes('fromLocation') && (
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: '#6b7280' }}>
                      From location
                      <select value={newRule.fromLocation} onChange={(e) => setNewRule({ ...newRule, fromLocation: e.target.value, toLocation: e.target.value === newRule.toLocation ? '' : newRule.toLocation })} style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', minWidth: '200px' }}>
                        <option value="">Select a location</option>
                        {locationNameOptions.map(location => <option key={location} value={location}>{location}</option>)}
                      </select>
                    </label>
                  )}
                  {activeRuleFields.includes('toLocation') && (
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: '#6b7280' }}>
                      To location
                      <select value={newRule.toLocation} onChange={(e) => setNewRule({ ...newRule, toLocation: e.target.value })} style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', minWidth: '200px' }}>
                        <option value="">Select a location</option>
                        {locationNameOptions.filter(location => location !== newRule.fromLocation).map(location => <option key={location} value={location}>{location}</option>)}
                      </select>
                    </label>
                  )}
                  {activeRuleFields.includes('targetYear') && (
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: '#6b7280' }}>
                      Year (optional)
                      <input type="number" min="2020" max="2100" value={newRule.targetYear} onChange={(e) => setNewRule({ ...newRule, targetYear: e.target.value })} placeholder={String(targetYear)} style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px', width: '110px' }} />
                    </label>
                  )}
                  {![RULE_TYPES.LOCATION_BLACKOUT, RULE_TYPES.LOCATION_REASSIGN].includes(newRule.type) && (
                    <label style={{ display: 'flex', flexDirection: 'column', gap: '4px', fontSize: '12px', color: '#6b7280' }}>
                      Severity
                      <select value={newRule.severity} onChange={(e) => setNewRule({ ...newRule, severity: e.target.value })} style={{ padding: '8px 10px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '13px' }}>
                        <option value="hard">Hard (enforced)</option>
                        <option value="soft">Soft (preference)</option>
                      </select>
                    </label>
                  )}
                  <button onClick={handleAddRule} disabled={!canAddRule} style={{ ...btnPrimary, backgroundColor: canAddRule ? '#8b5cf6' : '#9ca3af', cursor: canAddRule ? 'pointer' : 'not-allowed' }}>+ Add rule</button>
                </div>
                <p style={{ fontSize: '12px', color: '#9ca3af', margin: '10px 0 0' }}>{RULE_TYPE_META[newRule.type]?.help}</p>
              </div>
            </div>

            {/* AI planning assistant */}
            <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <h4 style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937', margin: 0 }}>🤖 AI Planning Assistant</h4>
                <button
                  onClick={requestPlanningAdvice}
                  disabled={planningLoading || !analysisData || !aiScheduleService.isConfigured}
                  style={{ ...btnPrimary, backgroundColor: (planningLoading || !analysisData || !aiScheduleService.isConfigured) ? '#9ca3af' : '#8b5cf6', cursor: (planningLoading || !analysisData || !aiScheduleService.isConfigured) ? 'not-allowed' : 'pointer' }}
                >
                  {planningLoading ? 'Reviewing...' : 'Review draft against rules'}
                </button>
              </div>

              {!aiScheduleService.isConfigured && (
                <div style={{ backgroundColor: '#fffbeb', border: '1px solid #fcd34d', borderRadius: '8px', padding: '12px', marginBottom: '12px' }}>
                  <p style={{ margin: 0, fontSize: '13px', color: '#78350f' }}>
                    AI assistant not configured. Set <code>REACT_APP_AI_ENDPOINT</code> and <code>REACT_APP_AI_API_KEY</code> to enable it.
                    Exclusion rules above still work without AI.
                  </p>
                </div>
              )}
              {!analysisData && (
                <p style={{ color: '#6b7280', fontSize: '13px', margin: 0 }}>Load analysis data first from the Analyze tab.</p>
              )}
              {planningAdvice && (
                <div style={{ maxHeight: '400px', overflowY: 'auto', fontSize: '14px', lineHeight: '1.6', color: '#374151' }}>
                  <p style={{ margin: '0 0 10px', fontSize: '12px', fontWeight: '600', color: '#5b21b6' }}>AI review only — enforced rules are applied by the generator, not by this response.</p>
                  <MarkdownContent content={planningAdvice} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* SAVED DRAFTS TAB */}
        {activeTab === 'drafts' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', margin: 0 }}>Saved Drafts</h3>
              <label style={{ ...btnSecondary, cursor: 'pointer' }}>
                📥 Import Draft
                <input type="file" accept=".json" onChange={handleImportDraft} style={{ display: 'none' }} />
              </label>
            </div>

            {savedDrafts.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '48px', color: '#6b7280' }}>
                <p>No saved drafts yet. Generate a draft and save it.</p>
              </div>
            ) : (
              <div style={{ display: 'grid', gap: '16px' }}>
                {savedDrafts.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt)).map(draft => (
                  <div key={draft.id} style={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', padding: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <div>
                        <h4 style={{ margin: '0 0 4px', fontSize: '16px', color: '#1f2937' }}>
                          {draft.name || `${draft.targetYear} Schedule Draft`}
                          {currentDraftId === draft.id && (
                            <span style={{ marginLeft: '8px', fontSize: '11px', color: '#1d4ed8', backgroundColor: '#dbeafe', padding: '2px 6px', borderRadius: '4px', fontWeight: '500' }}>
                              CURRENTLY EDITING
                            </span>
                          )}
                        </h4>
                        <p style={{ margin: 0, fontSize: '13px', color: '#6b7280' }}>
                          Year {draft.targetYear} · {draft.sessionCount ?? draft.sessions?.length ?? 0} sessions · By {draft.createdBy} · Last edited {new Date(draft.updatedAt).toLocaleString('en-GB')}
                          {(draft.conflictCount ?? draft.conflicts?.length ?? 0) > 0 && (
                            <span style={{ color: '#f59e0b', marginLeft: '8px' }}>⚠️ {draft.conflictCount ?? draft.conflicts.length} conflicts</span>
                          )}
                        </p>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button onClick={() => handleRestoreDraft(draft)} style={{ ...btnSecondary, backgroundColor: '#eff6ff', color: '#1d4ed8', borderColor: '#bfdbfe' }}>
                          {currentDraftId === draft.id ? 'Reload' : '✏️ Open & Edit'}
                        </button>
                        <button onClick={() => handleExportDraft(draft.id)} style={btnSecondary}>📊 Export Excel</button>
                        <button
                          onClick={() => handleExportRevenue(draft.id)}
                          disabled={revenueExportingId !== null}
                          style={{ ...btnSecondary, backgroundColor: '#ecfdf5', color: '#047857', borderColor: '#a7f3d0', opacity: revenueExportingId !== null ? 0.65 : 1, cursor: revenueExportingId !== null ? 'wait' : 'pointer' }}
                        >
                          {revenueExportingId === draft.id ? '⏳ Building Revenue Report…' : '💰 Revenue Report'}
                        </button>
                        <button onClick={() => handleDeleteDraft(draft.id)} style={{ ...btnSecondary, color: '#ef4444', borderColor: '#fecaca' }}>Delete</button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

// --- Sub-components ---

const DRAFT_MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

// Department color mapping — copied verbatim from Calendar.js for visual parity
// Shared with the algorithm (so suggestedDepartment matches a color)
const DRAFT_DEPARTMENT_COLORS = DEPARTMENT_COLORS;

const getDraftDepartmentColor = (item) => getDepartmentColor(item);

// Sizing constants — match the live Calendar exactly
const D_HEADER_H = 40;
const D_CELL_BASE = 96;
const D_EVENT_H = 24;
const D_EVENT_GAP = 2;
const D_GRID_GAP = 1;
const D_GRID_PAD = 4;
const D_ROW_INSET = 28;

const DraftCalendarView = ({ draftSessions, calendarDate, setCalendarDate, targetYear, conflicts, selectedSession, setSelectedSession, onRemoveSession, onUpdateSession, onApproveSession, onUnapproveSession, onPublishSingle, publishingSessionIds, approvalCounts, onBatchApprove, onPublishApproved, publishing, showAddForm, setShowAddForm, newSession, setNewSession, onAddSession, onSaveDraft, onSaveDraftAsNew, onNewDraft, currentDraftName, currentDraftId, analysisData }) => {
  // Filter state — mirrors live Calendar.js filter UX
  const [locationFilter, setLocationFilter] = useState([]);
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [campusFilter, setCampusFilter] = useState('all');
  const [classroomFilter, setClassroomFilter] = useState('all');
  const [instructorFilter, setInstructorFilter] = useState([]);

  // Build filter / autocomplete option lists from draftSessions plus the broader
  // analysisData (historical sessions + instructor roster), so editing the
  // suggested entries in the modal can offer real options not yet on the calendar.
  const filterOptions = useMemo(() => {
    const locs = new Set();
    const depts = new Set();
    const camps = new Set();
    const rooms = new Set();
    const insts = new Set();
    const collectFrom = (s) => {
      if (s.locationName) locs.add(s.locationName);
      if (s.departmentName || s.department) depts.add(s.departmentName || s.department);
      if (s.campusName || s.campus) camps.add(s.campusName || s.campus);
      if (s.classroomName || s.classroom) rooms.add(s.classroomName || s.classroom);
      if (s.instructor && s.instructor !== 'TBD') insts.add(s.instructor);
      if (Array.isArray(s.instructors)) s.instructors.forEach(n => n && insts.add(n));
    };
    draftSessions.forEach(collectFrom);
    if (analysisData?.sessions) analysisData.sessions.forEach(collectFrom);
    if (Array.isArray(analysisData?.locations)) {
      analysisData.locations.forEach(l => l?.name && locs.add(l.name));
    }
    if (Array.isArray(analysisData?.instructors)) {
      analysisData.instructors.forEach(i => i?.name && insts.add(i.name));
    }
    return {
      locations: [...locs].sort(),
      departments: [...depts].sort(),
      campuses: [...camps].sort(),
      classrooms: [...rooms].sort(),
      instructors: [...insts].sort()
    };
  }, [draftSessions, analysisData]);

  const year = calendarDate.getFullYear();
  const month = calendarDate.getMonth();
  // Cover adjacent years so December → January views and long classes use the
  // same non-instructional holiday dates as the generator.
  const holidayMap = useMemo(() => {
    return getHolidaysForYears([year - 1, year, year + 1, targetYear]);
  }, [year, targetYear]);

  const toLocalDate = (input) => {
    if (!input) return null;
    const isoMatch = String(input).match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (isoMatch) return new Date(+isoMatch[1], +isoMatch[2] - 1, +isoMatch[3]);
    const dt = new Date(input);
    return !Number.isNaN(dt.getTime()) ? new Date(dt.getFullYear(), dt.getMonth(), dt.getDate()) : null;
  };

  const calendarDays = useMemo(() => {
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startingDayOfWeek = firstDay.getDay();
    const daysInMonth = lastDay.getDate();
    const days = [];

    const prevMonth = new Date(year, month, 0);
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({ date: prevMonth.getDate() - i, isCurrentMonth: false, fullDate: new Date(year, month - 1, prevMonth.getDate() - i) });
    }
    for (let date = 1; date <= daysInMonth; date++) {
      days.push({ date, isCurrentMonth: true, fullDate: new Date(year, month, date) });
    }
    const remaining = 42 - days.length;
    for (let date = 1; date <= remaining; date++) {
      days.push({ date, isCurrentMonth: false, fullDate: new Date(year, month + 1, date) });
    }
    return days;
  }, [year, month]);

  const firstVisible = calendarDays[0]?.fullDate;
  const lastVisible = calendarDays[calendarDays.length - 1]?.fullDate;

  // Hide bundle children from the grid (they appear nested under their parent)
  // — matches Calendar.js childSessionIds filtering — and apply UI filters
  const visibleSessions = useMemo(() => {
    return draftSessions.filter(s => {
      if (s.isBundleChild || s.parentDraftId) return false;

      if (locationFilter.length > 0) {
        const loc = s.locationName || '';
        if (!locationFilter.includes(loc)) return false;
      }
      if (departmentFilter !== 'all') {
        const dept = s.departmentName || s.department || '';
        if (dept !== departmentFilter) return false;
      }
      if (campusFilter !== 'all') {
        const cmp = s.campusName || s.campus || '';
        if (cmp !== campusFilter) return false;
      }
      if (classroomFilter !== 'all') {
        const room = s.classroomName || s.classroom || '';
        if (room !== classroomFilter) return false;
      }
      if (instructorFilter.length > 0) {
        const inst = s.instructor || '';
        if (!instructorFilter.includes(inst)) return false;
      }
      return true;
    });
  }, [draftSessions, locationFilter, departmentFilter, campusFilter, classroomFilter, instructorFilter]);

  // Build event spans from instructional dates only. Multi-week classes are
  // split at weekends and holidays rather than appearing to teach through them.
  const eventSpans = useMemo(() => {
    const spans = [];
    visibleSessions.forEach(session => {
      const startLocal = toLocalDate(session.startDate);
      const endLocal = toLocalDate(session.endDate);
      if (!startLocal || !endLocal) return;

      const instructionalDates = getTeachingDatesInRange(startLocal, endLocal, holidayMap);
      const visibleDates = instructionalDates.filter(date => date >= firstVisible && date <= lastVisible);
      let segment = null;
      visibleDates.forEach(date => {
        const index = calendarDays.findIndex(day => day.fullDate.toDateString() === date.toDateString());
        if (index === -1) return;
        const row = Math.floor(index / 7);
        const col = index % 7;
        if (segment && segment.row === row && col === segment.endCol + 1) {
          segment.endCol = col;
        } else {
          if (segment) spans.push(segment);
          segment = {
            session,
            row,
            startCol: col,
            endCol: col,
            isFirstSegment: date.getTime() === instructionalDates[0]?.getTime(),
            isLastSegment: false
          };
        }
        segment.isLastSegment = date.getTime() === instructionalDates[instructionalDates.length - 1]?.getTime();
      });
      if (segment) spans.push(segment);
    });

    // De-dupe spans defensively — same as Calendar.js
    const seen = new Set();
    const unique = [];
    for (const sp of spans) {
      const key = `${sp.session.id}|${sp.row}|${sp.startCol}|${sp.endCol}`;
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(sp);
    }
    return unique;
  }, [visibleSessions, calendarDays, firstVisible, lastVisible, holidayMap]);

  // Lane packing — same algorithm as Calendar.js
  const laidOut = useMemo(() => {
    const rows = Array.from({ length: 6 }, () => []);
    eventSpans.forEach(s => rows[s.row].push(s));

    const placed = [];
    const rowMaxLanes = [];

    rows.forEach((rowSpans) => {
      rowSpans.sort((a, b) => (a.startCol - b.startCol) || (a.endCol - b.endCol));
      const lanes = [];
      rowSpans.forEach(span => {
        let laneIndex = lanes.findIndex(lastEnd => lastEnd < span.startCol);
        if (laneIndex === -1) { laneIndex = lanes.length; lanes.push(span.endCol); }
        else { lanes[laneIndex] = span.endCol; }
        placed.push({ ...span, lane: laneIndex });
      });
      rowMaxLanes.push(lanes.length || 1);
    });

    return { spans: placed, rowMaxLanes };
  }, [eventSpans]);

  // Dynamic row heights — same as Calendar.js
  const rowHeights = useMemo(() => {
    return (laidOut?.rowMaxLanes || Array(6).fill(1)).map(nLanes =>
      Math.max(D_CELL_BASE, D_ROW_INSET + (nLanes * D_EVENT_H) + ((nLanes - 1) * D_EVENT_GAP) + D_ROW_INSET)
    );
  }, [laidOut]);

  const gridTemplateRows = `${D_HEADER_H}px ${rowHeights.map(h => `${h}px`).join(' ')}`;

  const rowTopOffset = (rowIndex) => {
    const prevRowsHeight = rowHeights.slice(0, rowIndex).reduce((a, b) => a + b, 0);
    return D_GRID_PAD + D_HEADER_H + D_GRID_GAP + prevRowsHeight + (rowIndex * D_GRID_GAP);
  };

  const navigateMonth = (dir) => {
    const d = new Date(calendarDate);
    d.setMonth(d.getMonth() + dir);
    setCalendarDate(d);
  };

  const formatDateForInput = (date) => {
    return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
  };

  const formatDate = (dateString) => {
    const date = toLocalDate(dateString);
    if (!date) return '';
    return date.toLocaleDateString('en-US', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      {/* Active draft banner — shows which saved draft (if any) is being edited */}
      <div style={{
        backgroundColor: currentDraftId ? '#eff6ff' : '#fff7ed',
        border: `1px solid ${currentDraftId ? '#bfdbfe' : '#fed7aa'}`,
        borderRadius: '8px',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        gap: '12px',
        fontSize: '13px'
      }}>
        <span style={{ fontWeight: '600', color: currentDraftId ? '#1d4ed8' : '#9a3412' }}>
          {currentDraftId ? '📝 Editing:' : '⚠️ Unsaved draft'}
        </span>
        <span style={{ color: '#374151' }}>
          {currentDraftId ? currentDraftName : 'Changes will be lost unless you click Save Draft.'}
        </span>
        {currentDraftId && (
          <span style={{ color: '#6b7280', marginLeft: 'auto' }}>
            Click <strong>Save Changes</strong> to update this draft, or <strong>Save As New</strong> to fork a copy.
          </span>
        )}
      </div>

      {/* Calendar Container */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
        {/* Header */}
        <div style={{ padding: '24px', borderBottom: '1px solid #e5e7eb' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', color: '#111827' }}>
                {DRAFT_MONTH_NAMES[month]} {year}
              </h2>
              <div style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                <button onClick={() => navigateMonth(-1)} style={{ padding: '8px', borderRadius: '8px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', fontSize: '16px', color: '#6b7280' }}>
                  ◀
                </button>
                <button onClick={() => navigateMonth(1)} style={{ padding: '8px', borderRadius: '8px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', fontSize: '16px', color: '#6b7280' }}>
                  ▶
                </button>
                <button
                  onClick={() => setCalendarDate(new Date(targetYear, 0, 1))}
                  style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #d1d5db', backgroundColor: 'white', cursor: 'pointer', fontSize: '14px', fontWeight: '500', color: '#374151', marginLeft: '8px' }}
                >
                  Jan {targetYear}
                </button>
                <input
                  type="date"
                  value={formatDateForInput(calendarDate)}
                  onChange={(e) => { const d = new Date(e.target.value + 'T00:00:00'); if (!isNaN(d.getTime())) setCalendarDate(d); }}
                  style={{ padding: '6px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', color: '#374151', cursor: 'pointer', marginLeft: '8px' }}
                />
              </div>
            </div>
            <div style={{ display: 'flex', gap: '4px' }}>
              {DRAFT_MONTH_NAMES.map((m, i) => (
                <button
                  key={i}
                  onClick={() => setCalendarDate(new Date(targetYear, i, 1))}
                  style={{
                    padding: '4px 8px', border: 'none', borderRadius: '4px', fontSize: '11px', fontWeight: '500', cursor: 'pointer',
                    backgroundColor: month === i && year === targetYear ? '#8b5cf6' : 'transparent',
                    color: month === i && year === targetYear ? '#fff' : '#6b7280'
                  }}
                >
                  {m.slice(0, 3)}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Filter Bar — mirrors live Calendar */}
        <div style={{ padding: '16px 24px 0' }}>
          <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap', padding: '12px 16px', backgroundColor: '#f9fafb', borderRadius: '8px' }}>
            {/* Location (multi-select via native select with multiple) */}
            <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151', display: 'flex', flexDirection: 'column', minWidth: '160px' }}>
              <span style={{ marginBottom: '4px' }}>Location</span>
              <select
                multiple
                value={locationFilter}
                onChange={(e) => setLocationFilter(Array.from(e.target.selectedOptions, o => o.value))}
                style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', minHeight: '64px' }}
              >
                {filterOptions.locations.map(loc => (
                  <option key={loc} value={loc}>{loc}</option>
                ))}
              </select>
            </label>

            {/* Department (single-select) */}
            <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151', display: 'flex', flexDirection: 'column', minWidth: '160px' }}>
              <span style={{ marginBottom: '4px' }}>Department</span>
              <select
                value={departmentFilter}
                onChange={(e) => setDepartmentFilter(e.target.value)}
                style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
              >
                <option value="all">All Departments</option>
                {filterOptions.departments.map(d => (<option key={d} value={d}>{d}</option>))}
              </select>
            </label>

            {/* Campus */}
            <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151', display: 'flex', flexDirection: 'column', minWidth: '140px' }}>
              <span style={{ marginBottom: '4px' }}>Campus</span>
              <select
                value={campusFilter}
                onChange={(e) => setCampusFilter(e.target.value)}
                style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
              >
                <option value="all">All Campuses</option>
                {filterOptions.campuses.map(c => (<option key={c} value={c}>{c}</option>))}
              </select>
            </label>

            {/* Classroom */}
            <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151', display: 'flex', flexDirection: 'column', minWidth: '140px' }}>
              <span style={{ marginBottom: '4px' }}>Classroom</span>
              <select
                value={classroomFilter}
                onChange={(e) => setClassroomFilter(e.target.value)}
                style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px' }}
              >
                <option value="all">All Classrooms</option>
                {filterOptions.classrooms.map(r => (<option key={r} value={r}>{r}</option>))}
              </select>
            </label>

            {/* Instructor (multi-select) */}
            <label style={{ fontSize: '12px', fontWeight: '500', color: '#374151', display: 'flex', flexDirection: 'column', minWidth: '180px' }}>
              <span style={{ marginBottom: '4px' }}>Instructor</span>
              <select
                multiple
                value={instructorFilter}
                onChange={(e) => setInstructorFilter(Array.from(e.target.selectedOptions, o => o.value))}
                style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', minHeight: '64px' }}
              >
                {filterOptions.instructors.map(i => (<option key={i} value={i}>{i}</option>))}
              </select>
            </label>

            {/* Clear filters */}
            <button
              onClick={() => {
                setLocationFilter([]);
                setDepartmentFilter('all');
                setCampusFilter('all');
                setClassroomFilter('all');
                setInstructorFilter([]);
              }}
              style={{ padding: '6px 12px', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: '#fff', color: '#374151', fontSize: '12px', fontWeight: '500', cursor: 'pointer', height: '32px', alignSelf: 'flex-end' }}
            >
              Clear Filters
            </button>
          </div>
        </div>

        {/* Approval Summary Bar */}
        <div style={{ padding: '12px 24px 0' }}>
          <div style={{ display: 'flex', gap: '16px', alignItems: 'center', padding: '12px 16px', backgroundColor: '#f9fafb', borderRadius: '8px', marginBottom: '0' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#d1d5db' }} />
              <span style={{ fontSize: '13px', color: '#6b7280' }}>Pending: <strong style={{ color: '#374151' }}>{approvalCounts.pending}</strong></span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#22c55e' }} />
              <span style={{ fontSize: '13px', color: '#6b7280' }}>Approved: <strong style={{ color: '#166534' }}>{approvalCounts.approved}</strong></span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
              <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#3b82f6' }} />
              <span style={{ fontSize: '13px', color: '#6b7280' }}>Published: <strong style={{ color: '#1d4ed8' }}>{approvalCounts.published}</strong></span>
            </div>
            <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
              <button
                onClick={() => setShowAddForm(!showAddForm)}
                style={{ padding: '6px 14px', border: '1px solid #c4b5fd', borderRadius: '6px', backgroundColor: '#fff', color: '#8b5cf6', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}
              >
                {showAddForm ? '✕ Cancel' : '+ Add Session'}
              </button>
              {onNewDraft && (
                <button
                  onClick={onNewDraft}
                  title="Start a new blank draft (the next save will create a new record)"
                  style={{ padding: '6px 14px', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: '#fff', color: '#374151', fontSize: '13px', fontWeight: '500', cursor: 'pointer' }}
                >
                  📄 New Draft
                </button>
              )}
              {onSaveDraft && (
                <button
                  onClick={onSaveDraft}
                  disabled={draftSessions.length === 0}
                  title={currentDraftId
                    ? `Save changes to "${currentDraftName}"`
                    : 'Save this draft (you can re-open and continue editing it later)'}
                  style={{ padding: '6px 14px', border: '1px solid #93c5fd', borderRadius: '6px', backgroundColor: '#eff6ff', color: '#1d4ed8', fontSize: '13px', fontWeight: '500', cursor: draftSessions.length === 0 ? 'not-allowed' : 'pointer', opacity: draftSessions.length === 0 ? 0.5 : 1 }}
                >
                  {currentDraftId ? '💾 Save Changes' : '💾 Save Draft'}
                </button>
              )}
              {onSaveDraftAsNew && currentDraftId && (
                <button
                  onClick={onSaveDraftAsNew}
                  disabled={draftSessions.length === 0}
                  title="Save a separate copy of this draft under a new name"
                  style={{ padding: '6px 14px', border: '1px solid #c7d2fe', borderRadius: '6px', backgroundColor: '#eef2ff', color: '#4338ca', fontSize: '13px', fontWeight: '500', cursor: draftSessions.length === 0 ? 'not-allowed' : 'pointer', opacity: draftSessions.length === 0 ? 0.5 : 1 }}
                >
                  📑 Save As New
                </button>
              )}
              <button
                onClick={() => onBatchApprove(draftSessions.filter(s => !s.approvalStatus || s.approvalStatus === 'pending').map(s => s.id))}
                disabled={approvalCounts.pending === 0}
                style={{ padding: '6px 14px', border: '1px solid #bbf7d0', borderRadius: '6px', backgroundColor: '#f0fdf4', color: '#166534', fontSize: '13px', fontWeight: '500', cursor: approvalCounts.pending === 0 ? 'not-allowed' : 'pointer', opacity: approvalCounts.pending === 0 ? 0.5 : 1 }}
              >
                ✓ Approve All
              </button>
              <button
                onClick={onPublishApproved}
                disabled={PUSH_TO_PA_DISABLED || publishing || approvalCounts.approved === 0}
                title={PUSH_TO_PA_DISABLED ? 'Publishing to Power Apps is temporarily disabled (new course filter pending reconciliation)' : ''}
                style={{ padding: '6px 14px', border: 'none', borderRadius: '6px', backgroundColor: '#9ca3af', color: '#fff', fontSize: '13px', fontWeight: '500', cursor: 'not-allowed' }}
              >
                {PUSH_TO_PA_DISABLED ? '🔒 Publishing Disabled' : (publishing ? 'Publishing...' : `🚀 Publish ${approvalCounts.approved} Approved`)}
              </button>
            </div>
          </div>
        </div>

        {/* Add Session Form */}
        {showAddForm && (
          <div style={{ padding: '0 24px', marginTop: '12px' }}>
            <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '2px solid #c4b5fd', padding: '20px' }}>
              <h4 style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: '0 0 12px' }}>Add New Session</h4>
              <div style={{ display: 'flex', gap: '12px', alignItems: 'flex-end', flexWrap: 'wrap' }}>
                <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', flex: '1 1 200px' }}>
                  Course Name
                  <input
                    type="text"
                    value={newSession.name}
                    onChange={(e) => setNewSession(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g. STCW Basic Safety Training"
                    style={{ display: 'block', width: '100%', marginTop: '4px', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                </label>
                <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>
                  Start Date
                  <input
                    type="date"
                    value={newSession.startDate}
                    onChange={(e) => setNewSession(prev => ({ ...prev, startDate: e.target.value }))}
                    style={{ display: 'block', marginTop: '4px', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                  />
                </label>
                <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>
                  End Date
                  <input
                    type="date"
                    value={newSession.endDate}
                    onChange={(e) => setNewSession(prev => ({ ...prev, endDate: e.target.value }))}
                    style={{ display: 'block', marginTop: '4px', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                  />
                </label>
                <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151' }}>
                  Capacity
                  <input
                    type="number"
                    value={newSession.maxCapacity}
                    onChange={(e) => setNewSession(prev => ({ ...prev, maxCapacity: e.target.value }))}
                    style={{ display: 'block', marginTop: '4px', width: '80px', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                  />
                </label>
                <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', flex: '1 1 180px' }}>
                  Instructor
                  <input
                    type="text"
                    list="dcv-add-instructors"
                    value={newSession.instructor || ''}
                    onChange={(e) => setNewSession(prev => ({ ...prev, instructor: e.target.value }))}
                    placeholder="Assign instructor"
                    style={{ display: 'block', width: '100%', marginTop: '4px', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                  <datalist id="dcv-add-instructors">
                    {filterOptions.instructors.map(i => <option key={i} value={i} />)}
                  </datalist>
                </label>
                <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', flex: '1 1 160px' }}>
                  Department
                  <input
                    type="text"
                    list="dcv-add-departments"
                    value={newSession.department || ''}
                    onChange={(e) => setNewSession(prev => ({ ...prev, department: e.target.value }))}
                    placeholder="Department"
                    style={{ display: 'block', width: '100%', marginTop: '4px', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                  <datalist id="dcv-add-departments">
                    {filterOptions.departments.map(d => <option key={d} value={d} />)}
                  </datalist>
                </label>
                <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', flex: '1 1 160px' }}>
                  Location
                  <input
                    type="text"
                    list="dcv-add-locations"
                    value={newSession.locationName || ''}
                    onChange={(e) => {
                      const match = (analysisData?.locations || []).find(l => l.name === e.target.value);
                      setNewSession(prev => ({
                        ...prev,
                        locationName: e.target.value,
                        locationGuid: match ? match.id : prev.locationGuid
                      }));
                    }}
                    placeholder="Site / Location"
                    style={{ display: 'block', width: '100%', marginTop: '4px', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                  <datalist id="dcv-add-locations">
                    {filterOptions.locations.map(l => <option key={l} value={l} />)}
                  </datalist>
                </label>
                <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', flex: '1 1 140px' }}>
                  Classroom
                  <input
                    type="text"
                    list="dcv-add-classrooms"
                    value={newSession.classroom || ''}
                    onChange={(e) => setNewSession(prev => ({ ...prev, classroom: e.target.value }))}
                    placeholder="Classroom"
                    style={{ display: 'block', width: '100%', marginTop: '4px', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                  <datalist id="dcv-add-classrooms">
                    {filterOptions.classrooms.map(c => <option key={c} value={c} />)}
                  </datalist>
                </label>
                <label style={{ fontSize: '13px', fontWeight: '500', color: '#374151', flex: '1 1 140px' }}>
                  Campus
                  <input
                    type="text"
                    list="dcv-add-campuses"
                    value={newSession.campus || ''}
                    onChange={(e) => setNewSession(prev => ({ ...prev, campus: e.target.value }))}
                    placeholder="Campus"
                    style={{ display: 'block', width: '100%', marginTop: '4px', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' }}
                  />
                  <datalist id="dcv-add-campuses">
                    {filterOptions.campuses.map(c => <option key={c} value={c} />)}
                  </datalist>
                </label>
                <button
                  onClick={onAddSession}
                  disabled={!newSession.name.trim()}
                  style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', backgroundColor: !newSession.name.trim() ? '#9ca3af' : '#8b5cf6', color: '#fff', fontSize: '14px', fontWeight: '500', cursor: !newSession.name.trim() ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap' }}
                >
                  Add to Draft
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Calendar Grid */}
        <div style={{ padding: '24px', paddingTop: '16px' }}>
          <div style={{ position: 'relative' }}>
            {/* Grid: day headers + day cells */}
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gridTemplateRows: gridTemplateRows,
              gap: `${D_GRID_GAP}px`,
              backgroundColor: '#e5e7eb',
              padding: `${D_GRID_PAD}px`,
              borderRadius: '8px'
            }}>
              {/* Day Headers */}
              {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(day => (
                <div key={day} style={{ backgroundColor: '#f9fafb', padding: '12px', textAlign: 'center', fontSize: '14px', fontWeight: '500', color: '#374151', borderRadius: '4px', height: `${D_HEADER_H}px` }}>
                  {day}
                </div>
              ))}

              {/* Calendar Days */}
              {calendarDays.map((day, index) => {
                // Build local-date key (no UTC shift) so holiday lookup matches the algorithm
                const dy = day.fullDate.getFullYear();
                const dm = String(day.fullDate.getMonth() + 1).padStart(2, '0');
                const dd = String(day.fullDate.getDate()).padStart(2, '0');
                const dateKey = `${dy}-${dm}-${dd}`;
                const holidayName = holidayMap[dateKey];
                const baseBg = day.isCurrentMonth ? 'white' : '#f9fafb';
                return (
                  <div
                    key={index}
                    style={{
                      backgroundColor: holidayName ? '#fef2f2' : baseBg,
                      padding: '8px',
                      fontSize: '14px',
                      borderRadius: '4px',
                      border: 'none',
                      color: day.isCurrentMonth ? '#111827' : '#9ca3af',
                      position: 'relative'
                    }}
                    title={holidayName || ''}
                  >
                    <span style={{ fontWeight: '500' }}>
                      {day.date}
                    </span>
                    {holidayName && (
                      <div style={{
                        fontSize: '9px',
                        color: '#b91c1c',
                        fontWeight: '600',
                        textTransform: 'uppercase',
                        letterSpacing: '0.4px',
                        marginTop: '2px',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis'
                      }}>
                        🎉 {holidayName}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {/* Event Spans Overlay */}
            <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, pointerEvents: 'none', padding: `${D_GRID_PAD}px` }}>
              {laidOut.spans.map((span, index) => {
                const laneTop = rowTopOffset(span.row) + D_ROW_INSET + (span.lane * (D_EVENT_H + D_EVENT_GAP));
                const status = span.session.approvalStatus || 'pending';
                // Use the same department color map as the live Calendar
                const bg = status === 'published' ? '#94a3b8' : getDraftDepartmentColor(span.session);
                const radius =
                  span.isFirstSegment && span.isLastSegment ? '4px' :
                  span.isFirstSegment ? '4px 0 0 4px' :
                  span.isLastSegment ? '0 4px 4px 0' : '0';

                return (
                  <div
                    key={index}
                    style={{
                      position: 'absolute',
                      top: `${laneTop}px`,
                      left: `${(span.startCol / 7) * 100}%`,
                      width: `${((span.endCol - span.startCol + 1) / 7) * 100}%`,
                      height: `${D_EVENT_H}px`,
                      zIndex: 10,
                      pointerEvents: 'auto'
                    }}
                  >
                    <div
                      onClick={() => setSelectedSession(selectedSession?.id === span.session.id ? null : span.session)}
                      style={{
                        height: '100%',
                        padding: '0 8px',
                        fontSize: '12px',
                        color: 'white',
                        fontWeight: '500',
                        borderRadius: radius,
                        cursor: 'pointer',
                        overflow: 'hidden',
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: bg,
                        opacity: selectedSession && selectedSession.id !== span.session.id ? 0.4 : 1
                      }}
                      title={(() => {
                        const lines = [
                          span.session.name,
                          span.session.locationName ? `Location: ${span.session.locationName}` : 'Location: Unassigned',
                          `${span.session.startDate.split('T')[0]} → ${span.session.endDate.split('T')[0]}`
                        ];
                        if (span.session.needsReview && Array.isArray(span.session.reviewReasons)) {
                          lines.push('');
                          lines.push('⚠ Needs review:');
                          span.session.reviewReasons.forEach(r => lines.push(`  • ${r}`));
                        }
                        return lines.join('\n');
                      })()}
                    >
                      <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', width: '100%' }}>
                        {span.session.needsReview && (
                          <span style={{ marginRight: '4px' }} title="Needs review">⚠️</span>
                        )}
                        {status === 'approved' && '✓ '}
                        {status === 'published' && '✔ '}
                        {span.session.isBundleParent && '📦 '}
                        {span.session.name}
                        {span.session.locationName && ` · ${span.session.locationName}`}
                        {` (Cap: ${span.session.maxCapacity})`}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      {/* Session Details Modal — matches Calendar.js modal */}
      {selectedSession && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          padding: '16px', zIndex: 50, overflow: 'auto'
        }}>
          <div style={{
            backgroundColor: 'white', borderRadius: '8px',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
            maxWidth: 'min(800px, 90vw)', width: '100%',
            maxHeight: '90vh', overflow: 'auto', margin: 'auto'
          }}>
            {/* Modal Header */}
            <div style={{ padding: '24px', borderBottom: '1px solid #e5e7eb' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#111827' }}>
                  {selectedSession.name}
                </h3>
                <button onClick={() => setSelectedSession(null)} style={{ color: '#9ca3af', fontSize: '20px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer' }}>
                  ×
                </button>
              </div>
              <p style={{ fontSize: '14px', color: (selectedSession.approvalStatus === 'published' ? '#3b82f6' : selectedSession.approvalStatus === 'approved' ? '#22c55e' : '#8b5cf6'), fontWeight: '600', marginTop: '8px', textTransform: 'uppercase' }}>
                {selectedSession.approvalStatus === 'published' ? '✔ Published' : selectedSession.approvalStatus === 'approved' ? '✓ Approved' : 'Pending Approval'}
              </p>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ fontWeight: '500', color: '#111827', marginBottom: '8px' }}>Date</h4>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span style={{ color: '#6b7280', fontSize: '14px' }}>
                    {formatDate(selectedSession.startDate)}
                    {selectedSession.startDate.split('T')[0] !== selectedSession.endDate.split('T')[0] && ` - ${formatDate(selectedSession.endDate)}`}
                  </span>
                </div>
              </div>

              {/* Review banner — shown when the algorithm flagged this session
                  (cancelled history, missing department, classroom conflict). */}
              {selectedSession.needsReview && Array.isArray(selectedSession.reviewReasons) && selectedSession.reviewReasons.length > 0 && (
                <div style={{
                  marginBottom: '16px',
                  padding: '12px 14px',
                  backgroundColor: '#fef3c7',
                  border: '1px solid #fcd34d',
                  borderRadius: '6px'
                }}>
                  <div style={{ fontSize: '13px', fontWeight: 600, color: '#92400e', marginBottom: '6px' }}>
                    ⚠️ Needs review
                  </div>
                  <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '13px', color: '#78350f' }}>
                    {selectedSession.reviewReasons.map((r, i) => <li key={i}>{r}</li>)}
                  </ul>
                </div>
              )}

              {/* Editable suggested entries — values default to the AI/historical
                  suggestion but can be overridden inline. Datalists provide
                  autocomplete from the analysis data + current drafts. */}
              {(() => {
                const isPublished = selectedSession.approvalStatus === 'published';
                const inputStyle = {
                  width: '100%',
                  padding: '8px 12px',
                  border: '1px solid #d1d5db',
                  borderRadius: '6px',
                  fontSize: '14px',
                  boxSizing: 'border-box',
                  backgroundColor: isPublished ? '#f9fafb' : '#ffffff',
                  color: '#111827'
                };
                const labelStyle = { fontWeight: '500', color: '#111827', marginBottom: '4px', display: 'block' };
                const updateField = (fields) => {
                  Object.entries(fields).forEach(([k, v]) => onUpdateSession(selectedSession.id, k, v));
                  setSelectedSession(prev => ({ ...prev, ...fields }));
                };
                return (
                  <>
                    <div style={{ marginBottom: '16px' }}>
                      <label style={labelStyle}>Department</label>
                      <input
                        type="text"
                        list="dcv-departments"
                        value={selectedSession.department || selectedSession.departmentName || ''}
                        disabled={isPublished}
                        onChange={(e) => updateField({ department: e.target.value, departmentName: e.target.value })}
                        placeholder="e.g. Maritime Operations"
                        style={inputStyle}
                      />
                      <datalist id="dcv-departments">
                        {filterOptions.departments.map(d => <option key={d} value={d} />)}
                      </datalist>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={labelStyle}>Location</label>
                      <input
                        type="text"
                        list="dcv-locations"
                        value={selectedSession.locationName || ''}
                        disabled={isPublished}
                        onChange={(e) => {
                          // If user picks a name we know, also resync the GUID from analysisData
                          const match = (analysisData?.locations || []).find(l => l.name === e.target.value);
                          updateField({
                            locationName: e.target.value,
                            locationGuid: match ? match.id : selectedSession.locationGuid
                          });
                        }}
                        placeholder="Site / Location name"
                        style={inputStyle}
                      />
                      <datalist id="dcv-locations">
                        {filterOptions.locations.map(l => <option key={l} value={l} />)}
                      </datalist>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={labelStyle}>Campus</label>
                      <input
                        type="text"
                        list="dcv-campuses"
                        value={selectedSession.campus || selectedSession.campusName || ''}
                        disabled={isPublished}
                        onChange={(e) => updateField({ campus: e.target.value, campusName: e.target.value })}
                        placeholder="Billing campus"
                        style={inputStyle}
                      />
                      <datalist id="dcv-campuses">
                        {filterOptions.campuses.map(c => <option key={c} value={c} />)}
                      </datalist>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={labelStyle}>Classroom</label>
                      <input
                        type="text"
                        list="dcv-classrooms"
                        value={selectedSession.classroom || selectedSession.classroomName || ''}
                        disabled={isPublished}
                        onChange={(e) => updateField({ classroom: e.target.value, classroomName: e.target.value })}
                        placeholder="Specific classroom"
                        style={inputStyle}
                      />
                      <datalist id="dcv-classrooms">
                        {filterOptions.classrooms.map(c => <option key={c} value={c} />)}
                      </datalist>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={labelStyle}>
                        Instructor
                        {selectedSession.instructor && (
                          <span style={{ marginLeft: '8px', fontSize: '12px', color: '#8b5cf6', fontWeight: 400 }}>
                            (suggested from history)
                          </span>
                        )}
                      </label>
                      <input
                        type="text"
                        list="dcv-instructors"
                        value={selectedSession.instructor || ''}
                        disabled={isPublished}
                        onChange={(e) => updateField({ instructor: e.target.value, mi_instructor: e.target.value })}
                        placeholder="Assign an instructor"
                        style={inputStyle}
                      />
                      <datalist id="dcv-instructors">
                        {filterOptions.instructors.map(i => <option key={i} value={i} />)}
                      </datalist>
                    </div>

                    <div style={{ marginBottom: '16px' }}>
                      <label style={labelStyle}>Capacity</label>
                      <input
                        type="number"
                        min="0"
                        value={selectedSession.maxCapacity || 0}
                        disabled={isPublished}
                        onChange={(e) => {
                          const val = parseInt(e.target.value) || 0;
                          updateField({ maxCapacity: val });
                        }}
                        style={{ ...inputStyle, width: '120px' }}
                      />
                    </div>
                  </>
                );
              })()}

              <div style={{ marginBottom: '16px' }}>
                <h4 style={{ fontWeight: '500', color: '#111827', marginBottom: '8px' }}>Analytics</h4>
                <div style={{ display: 'flex', gap: '24px', fontSize: '14px', color: '#6b7280' }}>
                  <span>Avg Fill Rate: <strong style={{ color: selectedSession.avgFillRate >= 0.7 ? '#22c55e' : selectedSession.avgFillRate >= 0.4 ? '#f59e0b' : '#ef4444' }}>{Math.round((selectedSession.avgFillRate || 0) * 100)}%</strong></span>
                  <span>Confidence: <strong>{selectedSession.confidence}%</strong></span>
                  <span>Trend: <strong>{selectedSession.trend}</strong></span>
                </div>
              </div>

              {/* Bundle Sessions — mirrors Calendar.js bundle modal */}
              {selectedSession.childSessions && selectedSession.childSessions.length > 0 && (
                <div style={{ marginTop: '24px', borderTop: '1px solid #e5e7eb', paddingTop: '24px' }}>
                  <h4 style={{ fontWeight: '500', color: '#111827', marginBottom: '16px' }}>
                    Bundle Sessions ({selectedSession.childSessions.length})
                  </h4>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                    {selectedSession.childSessions.map((child, idx) => (
                      <div key={child.id || idx} style={{
                        backgroundColor: '#f9fafb',
                        padding: '16px',
                        borderRadius: '8px',
                        border: '1px solid #e5e7eb'
                      }}>
                        <h5 style={{ fontWeight: '600', color: '#111827', fontSize: '14px', margin: '0 0 8px' }}>
                          {child.name || 'Untitled Session'}
                        </h5>
                        <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>
                          <strong>Date:</strong>
                          <div>{formatDate(child.startDate)}</div>
                          {child.startDate.split('T')[0] !== child.endDate.split('T')[0] && (
                            <div>to {formatDate(child.endDate)}</div>
                          )}
                        </div>
                        {child.maxCapacity > 0 && (
                          <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '6px' }}>
                            <strong>Capacity:</strong> {child.maxCapacity} seats
                          </div>
                        )}
                        {child.department && (
                          <div style={{ fontSize: '12px', color: '#6b7280' }}>
                            <strong>Department:</strong> {child.department}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Date edit controls — kept separate from the suggested-entries
                  block so date pickers stay aligned in one row. */}
              {selectedSession.approvalStatus !== 'published' && (
              <div style={{ marginTop: '24px', borderTop: '1px solid #e5e7eb', paddingTop: '24px' }}>
                <h4 style={{ fontWeight: '500', color: '#111827', marginBottom: '16px' }}>Reschedule</h4>
                <div style={{ display: 'flex', gap: '16px', alignItems: 'center', flexWrap: 'wrap' }}>
                  <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Start
                    <input type="date" value={selectedSession.startDate.split('T')[0]}
                      onChange={(e) => { onUpdateSession(selectedSession.id, 'startDate', e.target.value + 'T09:00:00.000Z'); setSelectedSession(prev => ({ ...prev, startDate: e.target.value + 'T09:00:00.000Z' })); }}
                      style={{ display: 'block', marginTop: '4px', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                    />
                  </label>
                  <label style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>End
                    <input type="date" value={selectedSession.endDate.split('T')[0]}
                      onChange={(e) => { onUpdateSession(selectedSession.id, 'endDate', e.target.value + 'T17:00:00.000Z'); setSelectedSession(prev => ({ ...prev, endDate: e.target.value + 'T17:00:00.000Z' })); }}
                      style={{ display: 'block', marginTop: '4px', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '14px' }}
                    />
                  </label>
                </div>
              </div>
              )}

              {/* Actions */}
              <div style={{ marginTop: '24px', display: 'flex', gap: '8px', justifyContent: 'flex-end' }}>
                {(!selectedSession.approvalStatus || selectedSession.approvalStatus === 'pending') && (
                  <button onClick={() => { onApproveSession(selectedSession.id); setSelectedSession(prev => ({ ...prev, approvalStatus: 'approved' })); }}
                    style={{ padding: '8px 16px', border: '1px solid #bbf7d0', borderRadius: '6px', backgroundColor: '#f0fdf4', color: '#166534', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
                    ✓ Approve
                  </button>
                )}
                {selectedSession.approvalStatus === 'approved' && (
                  <>
                    <button onClick={() => { onUnapproveSession(selectedSession.id); setSelectedSession(prev => ({ ...prev, approvalStatus: 'pending' })); }}
                      style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: '#fff', color: '#374151', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
                      Unapprove
                    </button>
                    <button onClick={() => { onPublishSingle(selectedSession); }}
                      disabled={PUSH_TO_PA_DISABLED || publishingSessionIds.has(selectedSession.id)}
                      title={PUSH_TO_PA_DISABLED ? 'Publishing to Power Apps is temporarily disabled (new course filter pending reconciliation)' : ''}
                      style={{ padding: '8px 16px', border: 'none', borderRadius: '6px', backgroundColor: '#9ca3af', color: '#fff', fontSize: '14px', fontWeight: '500', cursor: 'not-allowed' }}>
                      {PUSH_TO_PA_DISABLED ? '🔒 Publishing Disabled' : (publishingSessionIds.has(selectedSession.id) ? 'Publishing...' : '🚀 Publish to Dataverse')}
                    </button>
                  </>
                )}
                {selectedSession.approvalStatus !== 'published' && (
                  <button onClick={() => { onRemoveSession(selectedSession.id); setSelectedSession(null); }}
                    style={{ padding: '8px 16px', border: '1px solid #fecaca', borderRadius: '6px', backgroundColor: '#fff', color: '#ef4444', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
                    Remove
                  </button>
                )}
                <button onClick={() => setSelectedSession(null)}
                  style={{ padding: '8px 16px', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: '#fff', color: '#374151', fontSize: '14px', fontWeight: '500', cursor: 'pointer' }}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Department Color Legend — matches live Calendar */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', padding: '16px 24px' }}>
        <h4 style={{ fontSize: '14px', fontWeight: '500', color: '#374151', margin: '0 0 12px' }}>Department Legend</h4>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
          {Object.entries(DRAFT_DEPARTMENT_COLORS).map(([name, color]) => (
            <div key={name} style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '13px', color: '#374151' }}>
              <div style={{ width: '12px', height: '12px', borderRadius: '3px', backgroundColor: color }} />
              <span>{name}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const SummaryCard = ({ label, value, color }) => (
  <div style={{ backgroundColor: '#ffffff', borderRadius: '8px', border: '1px solid #e5e7eb', borderTop: `3px solid ${color}`, padding: '16px' }}>
    <p style={{ fontSize: '12px', color: '#6b7280', margin: '0 0 4px', fontWeight: '500' }}>{label}</p>
    <p style={{ fontSize: '24px', fontWeight: '700', color: '#1f2937', margin: 0 }}>{value}</p>
  </div>
);

const TrendBadge = ({ trend }) => {
  const config = {
    increasing: { bg: '#dcfce7', color: '#166534', label: '↑ Up' },
    decreasing: { bg: '#fef2f2', color: '#991b1b', label: '↓ Down' },
    stable: { bg: '#f3f4f6', color: '#374151', label: '→ Stable' }
  };
  const c = config[trend] || config.stable;
  return (
    <span style={{ backgroundColor: c.bg, color: c.color, padding: '2px 8px', borderRadius: '10px', fontSize: '12px', fontWeight: '500' }}>
      {c.label}
    </span>
  );
};

const ConfidenceBadge = ({ value }) => {
  const color = value >= 70 ? '#22c55e' : value >= 40 ? '#f59e0b' : '#ef4444';
  return (
    <span style={{ color, fontWeight: '600', fontSize: '13px' }}>{value}%</span>
  );
};

// Animated loading spinner. Keyframes are injected inline so no shared CSS is
// touched; duplicate @keyframes blocks are harmless if rendered more than once.
const Spinner = ({ size = 40 }) => (
  <>
    <style>{'@keyframes sg-spin { to { transform: rotate(360deg); } }'}</style>
    <div
      role="status"
      aria-label="Loading"
      style={{
        width: size,
        height: size,
        margin: '0 auto',
        borderRadius: '50%',
        border: `${Math.max(3, Math.round(size / 10))}px solid #ede9fe`,
        borderTopColor: '#8b5cf6',
        animation: 'sg-spin 0.8s linear infinite'
      }}
    />
  </>
);

const UtilBar = ({ label, count, total, color }) => {
  const pct = total > 0 ? (count / total) * 100 : 0;
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '4px' }}>
        <span style={{ color: '#374151' }}>{label}</span>
        <span style={{ fontWeight: '600', color: '#374151' }}>{count}</span>
      </div>
      <div style={{ height: '8px', backgroundColor: '#f3f4f6', borderRadius: '4px' }}>
        <div style={{ height: '100%', width: `${pct}%`, backgroundColor: color, borderRadius: '4px' }} />
      </div>
    </div>
  );
};

// Simple markdown renderer for AI responses
const MarkdownContent = ({ content }) => {
  const lines = content.split('\n');
  return (
    <div>
      {lines.map((line, i) => {
        if (line.startsWith('### ')) return <h4 key={i} style={{ fontSize: '14px', fontWeight: '600', color: '#1f2937', margin: '16px 0 8px' }}>{line.slice(4)}</h4>;
        if (line.startsWith('## ')) return <h3 key={i} style={{ fontSize: '15px', fontWeight: '600', color: '#1f2937', margin: '16px 0 8px' }}>{line.slice(3)}</h3>;
        if (line.startsWith('# ')) return <h2 key={i} style={{ fontSize: '16px', fontWeight: '700', color: '#1f2937', margin: '16px 0 8px' }}>{line.slice(2)}</h2>;
        if (line.startsWith('- ') || line.startsWith('* ')) return <li key={i} style={{ marginLeft: '16px', marginBottom: '4px' }}>{line.slice(2)}</li>;
        if (line.startsWith('**') && line.endsWith('**')) return <p key={i} style={{ fontWeight: '600', margin: '8px 0 4px' }}>{line.slice(2, -2)}</p>;
        if (line.trim() === '') return <br key={i} />;
        return <p key={i} style={{ margin: '4px 0' }}>{line}</p>;
      })}
    </div>
  );
};

// --- Shared styles ---
const thStyle = { textAlign: 'left', padding: '8px', fontSize: '13px', color: '#6b7280', fontWeight: '500' };
const tdStyle = { padding: '8px', fontSize: '14px', color: '#1f2937' };
const btnPrimary = { padding: '8px 16px', border: 'none', borderRadius: '6px', color: '#fff', fontSize: '14px', fontWeight: '500', cursor: 'pointer' };
const btnSecondary = { padding: '6px 14px', border: '1px solid #d1d5db', borderRadius: '6px', backgroundColor: '#fff', color: '#374151', fontSize: '13px', fontWeight: '500', cursor: 'pointer' };

export default ScheduleGenerator;
